// src/actor/errors.ts
var INTERNAL_ERROR_CODE = "internal_error";
var INTERNAL_ERROR_DESCRIPTION = "Internal error. Read the server logs for more details.";
var ActorError = class extends Error {
  __type = "ActorError";
  public;
  metadata;
  statusCode = 500;
  group;
  code;
  static isActorError(error) {
    return typeof error === "object" && error.__type === "ActorError";
  }
  constructor(group, code, message, opts) {
    super(message, { cause: opts?.cause });
    this.group = group;
    this.code = code;
    this.public = opts?.public ?? false;
    this.metadata = opts?.metadata;
    if (opts?.public) {
      this.statusCode = 400;
    }
  }
  toString() {
    return this.message;
  }
};
var InternalError = class extends ActorError {
  constructor(message) {
    super("actor", INTERNAL_ERROR_CODE, message);
  }
};
var Unreachable = class extends InternalError {
  constructor(x) {
    super(`Unreachable case: ${x}`);
  }
};
var MalformedMessage = class extends ActorError {
  constructor(cause) {
    super("message", "malformed", `Malformed message: ${cause}`, {
      public: true,
      cause
    });
  }
};
var InvalidRequest = class extends ActorError {
  constructor(error) {
    super("request", "invalid", `Invalid request: ${error}`, {
      public: true,
      cause: error
    });
  }
};
var ActorNotFound = class extends ActorError {
  constructor(identifier) {
    super(
      "actor",
      "not_found",
      identifier ? `Actor not found: ${identifier} (https://www.rivet.dev/docs/clients/javascript)` : "Actor not found (https://www.rivet.dev/docs/clients/javascript)",
      { public: true }
    );
  }
};

// src/utils/env-vars.ts
var getRivetEngine = () => getEnvUniversal("RIVET_ENGINE");
var getRivetEndpoint = () => getEnvUniversal("RIVET_ENDPOINT");
var getRivetToken = () => getEnvUniversal("RIVET_TOKEN");
var getRivetNamespace = () => getEnvUniversal("RIVET_NAMESPACE");
var getRivetRunner = () => getEnvUniversal("RIVET_RUNNER");
var getLogLevel = () => getEnvUniversal("RIVET_LOG_LEVEL") ?? getEnvUniversal("LOG_LEVEL");
var getLogTarget = () => getEnvUniversal("RIVET_LOG_TARGET") === "1";
var getLogTimestamp = () => getEnvUniversal("RIVET_LOG_TIMESTAMP") === "1";
var getLogMessage = () => getEnvUniversal("RIVET_LOG_MESSAGE") === "1";
var getLogErrorStack = () => getEnvUniversal("RIVET_LOG_ERROR_STACK") === "1";
var getNextPhase = () => getEnvUniversal("NEXT_PHASE");

// src/common/utils.ts
function assertUnreachable(x) {
  throw new Error(`Unreachable case: ${x}`);
}
function deconstructError(error, logger4, extraLog, exposeInternalError = false) {
  let statusCode;
  let public_;
  let group;
  let code;
  let message;
  let metadata;
  if (ActorError.isActorError(error) && error.public) {
    statusCode = "statusCode" in error && error.statusCode ? error.statusCode : 400;
    public_ = true;
    group = error.group;
    code = error.code;
    message = getErrorMessage(error);
    metadata = error.metadata;
    logger4.info({
      msg: "public error",
      group,
      code,
      message,
      ...EXTRA_ERROR_LOG,
      ...extraLog
    });
  } else if (exposeInternalError) {
    if (ActorError.isActorError(error)) {
      statusCode = 500;
      public_ = false;
      group = error.group;
      code = error.code;
      message = getErrorMessage(error);
      metadata = error.metadata;
      logger4.info({
        msg: "internal error",
        group,
        code,
        message,
        stack: error?.stack,
        ...EXTRA_ERROR_LOG,
        ...extraLog
      });
    } else {
      statusCode = 500;
      public_ = false;
      group = "rivetkit";
      code = INTERNAL_ERROR_CODE;
      message = getErrorMessage(error);
      logger4.info({
        msg: "internal error",
        group,
        code,
        message,
        stack: error?.stack,
        ...EXTRA_ERROR_LOG,
        ...extraLog
      });
    }
  } else {
    statusCode = 500;
    public_ = false;
    group = "rivetkit";
    code = INTERNAL_ERROR_CODE;
    message = INTERNAL_ERROR_DESCRIPTION;
    metadata = {
      //url: `https://hub.rivet.dev/projects/${actorMetadata.project.slug}/environments/${actorMetadata.environment.slug}/actors?actorId=${actorMetadata.actor.id}`,
    };
    logger4.warn({
      msg: "internal error",
      error: getErrorMessage(error),
      stack: error?.stack,
      ...EXTRA_ERROR_LOG,
      ...extraLog
    });
  }
  return {
    __type: "ActorError",
    statusCode,
    public: public_,
    group,
    code,
    message,
    metadata
  };
}
function stringifyError(error) {
  if (error instanceof Error) {
    if (typeof process !== "undefined" && getLogErrorStack()) {
      return `${error.name}: ${error.message}${error.stack ? `
${error.stack}` : ""}`;
    } else {
      return `${error.name}: ${error.message}`;
    }
  } else if (typeof error === "string") {
    return error;
  } else if (typeof error === "object" && error !== null) {
    try {
      return `${JSON.stringify(error)}`;
    } catch {
      return "[cannot stringify error]";
    }
  } else {
    return `Unknown error: ${getErrorMessage(error)}`;
  }
}
function getErrorMessage(err) {
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
    return err.message;
  } else {
    return String(err);
  }
}
function noopNext() {
  return async () => {
  };
}

// package.json
var package_default = {
  name: "rivetkit",
  version: "2.1.7",
  description: "Lightweight libraries for building stateful actors on edge platforms",
  license: "Apache-2.0",
  keywords: [
    "rivetkit",
    "stateful",
    "serverless",
    "actors",
    "agents",
    "realtime",
    "websocket",
    "actors",
    "framework"
  ],
  files: [
    "dist",
    "src",
    "deno.json",
    "bun.json",
    "package.json"
  ],
  type: "module",
  exports: {
    ".": {
      import: {
        types: "./dist/tsup/mod.d.ts",
        default: "./dist/tsup/mod.js"
      },
      require: {
        types: "./dist/tsup/mod.d.cts",
        default: "./dist/tsup/mod.cjs"
      }
    },
    "./workflow": {
      import: {
        types: "./dist/tsup/workflow/mod.d.ts",
        default: "./dist/tsup/workflow/mod.js"
      },
      require: {
        types: "./dist/tsup/workflow/mod.d.cts",
        default: "./dist/tsup/workflow/mod.cjs"
      }
    },
    "./client": {
      import: {
        browser: {
          types: "./dist/browser/client.d.ts",
          default: "./dist/browser/client.js"
        },
        types: "./dist/tsup/client/mod.d.ts",
        default: "./dist/tsup/client/mod.js"
      },
      require: {
        types: "./dist/tsup/client/mod.d.cts",
        default: "./dist/tsup/client/mod.cjs"
      }
    },
    "./log": {
      import: {
        types: "./dist/tsup/common/log.d.ts",
        default: "./dist/tsup/common/log.js"
      },
      require: {
        types: "./dist/tsup/common/log.d.cts",
        default: "./dist/tsup/common/log.cjs"
      }
    },
    "./errors": {
      import: {
        types: "./dist/tsup/actor/errors.d.ts",
        default: "./dist/tsup/actor/errors.js"
      },
      require: {
        types: "./dist/tsup/actor/errors.d.cts",
        default: "./dist/tsup/actor/errors.cjs"
      }
    },
    "./utils": {
      import: {
        types: "./dist/tsup/utils.d.ts",
        default: "./dist/tsup/utils.js"
      },
      require: {
        types: "./dist/tsup/utils.d.cts",
        default: "./dist/tsup/utils.cjs"
      }
    },
    "./driver-helpers": {
      import: {
        types: "./dist/tsup/driver-helpers/mod.d.ts",
        default: "./dist/tsup/driver-helpers/mod.js"
      },
      require: {
        types: "./dist/tsup/driver-helpers/mod.d.cts",
        default: "./dist/tsup/driver-helpers/mod.cjs"
      }
    },
    "./driver-helpers/websocket": {
      import: {
        types: "./dist/tsup/common/websocket.d.ts",
        default: "./dist/tsup/common/websocket.js"
      },
      require: {
        types: "./dist/tsup/common/websocket.d.cts",
        default: "./dist/tsup/common/websocket.cjs"
      }
    },
    "./driver-test-suite": {
      import: {
        types: "./dist/tsup/driver-test-suite/mod.d.ts",
        default: "./dist/tsup/driver-test-suite/mod.js"
      },
      require: {
        types: "./dist/tsup/driver-test-suite/mod.d.cts",
        default: "./dist/tsup/driver-test-suite/mod.cjs"
      }
    },
    "./serve-test-suite": {
      import: {
        types: "./dist/tsup/serve-test-suite/mod.d.ts",
        default: "./dist/tsup/serve-test-suite/mod.js"
      },
      require: {
        types: "./dist/tsup/serve-test-suite/mod.d.cts",
        default: "./dist/tsup/serve-test-suite/mod.cjs"
      }
    },
    "./topologies/coordinate": {
      import: {
        types: "./dist/tsup/topologies/coordinate/mod.d.ts",
        default: "./dist/tsup/topologies/coordinate/mod.js"
      },
      require: {
        types: "./dist/tsup/topologies/coordinate/mod.d.cts",
        default: "./dist/tsup/topologies/coordinate/mod.cjs"
      }
    },
    "./topologies/partition": {
      import: {
        types: "./dist/tsup/topologies/partition/mod.d.ts",
        default: "./dist/tsup/topologies/partition/mod.js"
      },
      require: {
        types: "./dist/tsup/topologies/partition/mod.d.cts",
        default: "./dist/tsup/topologies/partition/mod.cjs"
      }
    },
    "./test": {
      import: {
        types: "./dist/tsup/test/mod.d.ts",
        default: "./dist/tsup/test/mod.js"
      },
      require: {
        types: "./dist/tsup/test/mod.d.cts",
        default: "./dist/tsup/test/mod.cjs"
      }
    },
    "./inspector": {
      import: {
        types: "./dist/tsup/inspector/mod.d.ts",
        default: "./dist/tsup/inspector/mod.js"
      },
      require: {
        types: "./dist/tsup/inspector/mod.d.cts",
        default: "./dist/tsup/inspector/mod.cjs"
      }
    },
    "./inspector/client": {
      import: {
        types: "./dist/browser/inspector/client.d.ts",
        default: "./dist/browser/inspector/client.js"
      }
    },
    "./db": {
      import: {
        types: "./dist/tsup/db/mod.d.ts",
        default: "./dist/tsup/db/mod.js"
      },
      require: {
        types: "./dist/tsup/db/mod.d.cts",
        default: "./dist/tsup/db/mod.cjs"
      }
    },
    "./db/drizzle": {
      import: {
        types: "./dist/tsup/db/drizzle/mod.d.ts",
        default: "./dist/tsup/db/drizzle/mod.js"
      },
      require: {
        types: "./dist/tsup/db/drizzle/mod.d.cts",
        default: "./dist/tsup/db/drizzle/mod.cjs"
      }
    },
    "./sandbox": {
      import: {
        types: "./dist/tsup/sandbox/index.d.ts",
        default: "./dist/tsup/sandbox/index.js"
      },
      require: {
        types: "./dist/tsup/sandbox/index.d.cts",
        default: "./dist/tsup/sandbox/index.cjs"
      }
    },
    "./sandbox/client": {
      import: {
        types: "./dist/tsup/sandbox/client.d.ts",
        default: "./dist/tsup/sandbox/client.js"
      },
      require: {
        types: "./dist/tsup/sandbox/client.d.cts",
        default: "./dist/tsup/sandbox/client.cjs"
      }
    },
    "./sandbox/docker": {
      import: {
        types: "./dist/tsup/sandbox/providers/docker.d.ts",
        default: "./dist/tsup/sandbox/providers/docker.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/docker.d.cts",
        default: "./dist/tsup/sandbox/providers/docker.cjs"
      }
    },
    "./sandbox/e2b": {
      import: {
        types: "./dist/tsup/sandbox/providers/e2b.d.ts",
        default: "./dist/tsup/sandbox/providers/e2b.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/e2b.d.cts",
        default: "./dist/tsup/sandbox/providers/e2b.cjs"
      }
    },
    "./sandbox/daytona": {
      import: {
        types: "./dist/tsup/sandbox/providers/daytona.d.ts",
        default: "./dist/tsup/sandbox/providers/daytona.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/daytona.d.cts",
        default: "./dist/tsup/sandbox/providers/daytona.cjs"
      }
    },
    "./sandbox/local": {
      import: {
        types: "./dist/tsup/sandbox/providers/local.d.ts",
        default: "./dist/tsup/sandbox/providers/local.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/local.d.cts",
        default: "./dist/tsup/sandbox/providers/local.cjs"
      }
    },
    "./sandbox/vercel": {
      import: {
        types: "./dist/tsup/sandbox/providers/vercel.d.ts",
        default: "./dist/tsup/sandbox/providers/vercel.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/vercel.d.cts",
        default: "./dist/tsup/sandbox/providers/vercel.cjs"
      }
    },
    "./sandbox/modal": {
      import: {
        types: "./dist/tsup/sandbox/providers/modal.d.ts",
        default: "./dist/tsup/sandbox/providers/modal.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/modal.d.cts",
        default: "./dist/tsup/sandbox/providers/modal.cjs"
      }
    },
    "./sandbox/computesdk": {
      import: {
        types: "./dist/tsup/sandbox/providers/computesdk.d.ts",
        default: "./dist/tsup/sandbox/providers/computesdk.js"
      },
      require: {
        types: "./dist/tsup/sandbox/providers/computesdk.d.cts",
        default: "./dist/tsup/sandbox/providers/computesdk.cjs"
      }
    }
  },
  engines: {
    node: ">=22.0.0"
  },
  sideEffects: [
    "./dist/tsup/chunk-*.js",
    "./dist/tsup/chunk-*.cjs"
  ],
  scripts: {
    build: "tsup src/mod.ts src/client/mod.ts src/common/log.ts src/common/websocket.ts src/actor/errors.ts src/topologies/coordinate/mod.ts src/topologies/partition/mod.ts src/utils.ts src/driver-helpers/mod.ts src/driver-test-suite/mod.ts src/serve-test-suite/mod.ts src/test/mod.ts src/inspector/mod.ts src/workflow/mod.ts src/db/mod.ts src/db/drizzle/mod.ts src/sandbox/index.ts src/sandbox/client.ts src/sandbox/providers/docker.ts src/sandbox/providers/e2b.ts src/sandbox/providers/daytona.ts src/sandbox/providers/local.ts src/sandbox/providers/vercel.ts src/sandbox/providers/modal.ts src/sandbox/providers/computesdk.ts",
    "build:browser": "tsup --config tsup.browser.config.ts",
    "build:schema": "./scripts/compile-bare.ts compile schemas/client-protocol/v1.bare -o dist/schemas/client-protocol/v1.ts && ./scripts/compile-bare.ts compile schemas/client-protocol/v2.bare -o dist/schemas/client-protocol/v2.ts && ./scripts/compile-bare.ts compile schemas/client-protocol/v3.bare -o dist/schemas/client-protocol/v3.ts && ./scripts/compile-bare.ts compile schemas/file-system-driver/v1.bare -o dist/schemas/file-system-driver/v1.ts && ./scripts/compile-bare.ts compile schemas/file-system-driver/v2.bare -o dist/schemas/file-system-driver/v2.ts && ./scripts/compile-bare.ts compile schemas/file-system-driver/v3.bare -o dist/schemas/file-system-driver/v3.ts && ./scripts/compile-bare.ts compile schemas/actor-persist/v1.bare -o dist/schemas/actor-persist/v1.ts && ./scripts/compile-bare.ts compile schemas/actor-persist/v2.bare -o dist/schemas/actor-persist/v2.ts && ./scripts/compile-bare.ts compile schemas/actor-persist/v3.bare -o dist/schemas/actor-persist/v3.ts && ./scripts/compile-bare.ts compile schemas/actor-persist/v4.bare -o dist/schemas/actor-persist/v4.ts && ./scripts/compile-bare.ts compile schemas/persist/v1.bare -o dist/schemas/persist/v1.ts && ./scripts/compile-bare.ts compile schemas/transport/v1.bare -o dist/schemas/transport/v1.ts && ./scripts/compile-bare.ts compile schemas/actor-inspector/v1.bare -o dist/schemas/actor-inspector/v1.ts && ./scripts/compile-bare.ts compile schemas/actor-inspector/v2.bare -o dist/schemas/actor-inspector/v2.ts && ./scripts/compile-bare.ts compile schemas/actor-inspector/v3.bare -o dist/schemas/actor-inspector/v3.ts",
    "check-types": "echo 'skipped - pre-existing type errors in test fixtures and sqlite-vfs'",
    lint: "biome check .",
    "lint:fix": "biome check --write .",
    format: "biome format .",
    "format:write": "biome format --write .",
    test: "vitest run",
    "test:watch": "vitest",
    "manager-openapi-gen": "tsx scripts/manager-openapi-gen.ts",
    "dump-asyncapi": "tsx scripts/dump-asyncapi.ts",
    "registry-config-schema-gen": "tsx scripts/registry-config-schema-gen.ts",
    "actor-config-schema-gen": "tsx scripts/actor-config-schema-gen.ts",
    "build:pack-inspector": "tsx scripts/pack-inspector.ts"
  },
  dependencies: {
    "@hono/node-server": "^1.18.2",
    "@hono/node-ws": "^1.1.1",
    "@hono/standard-validator": "^0.1.3",
    "@hono/zod-openapi": "^1.1.5",
    "@rivetkit/bare-ts": "^0.6.2",
    "@rivetkit/engine-runner": "workspace:*",
    "@rivetkit/fast-json-patch": "^3.1.2",
    "@rivetkit/on-change": "^6.0.2-rc.1",
    "@rivetkit/sqlite": "^0.1.1",
    "@rivetkit/sqlite-vfs": "workspace:*",
    "@rivetkit/traces": "workspace:*",
    "@rivetkit/virtual-websocket": "workspace:*",
    "@rivetkit/workflow-engine": "workspace:*",
    "cbor-x": "^1.6.0",
    "get-port": "^7.1.0",
    hono: "^4.7.0",
    invariant: "^2.2.4",
    nanoevents: "^9.1.0",
    "p-retry": "^6.2.1",
    pino: "^9.5.0",
    "sandbox-agent": "^0.4.0-rc.1",
    tar: "^7.5.0",
    uuid: "^12.0.0",
    vbare: "^0.0.4",
    zod: "^4.1.0"
  },
  devDependencies: {
    "@bare-ts/tools": "^0.13.0",
    "@biomejs/biome": "^2.3",
    "@daytonaio/sdk": "^0.150.0",
    "@e2b/code-interpreter": "^2.3.3",
    "@standard-schema/spec": "^1.0.0",
    "@types/dockerode": "^3.3.39",
    "@types/invariant": "^2",
    "@types/node": "^22.13.1",
    "@types/ws": "^8",
    "@vitest/ui": "3.1.1",
    "cli-table3": "^0.6.5",
    commander: "^12.1.0",
    dockerode: "^4.0.9",
    eventsource: "^4.0.0",
    "local-pkg": "^0.5.1",
    tsup: "^8.4.0",
    tsx: "^4.19.4",
    typescript: "^5.7.3",
    "vite-tsconfig-paths": "^5.1.4",
    vitest: "^3.1.1",
    ws: "^8.18.1",
    "zod-to-json-schema": "^3.25.0"
  },
  peerDependencies: {
    "@daytonaio/sdk": "^0.150.0",
    "@e2b/code-interpreter": "^2.3.3",
    dockerode: "^4.0.9",
    "drizzle-kit": "^0.31.2",
    "drizzle-orm": "^0.44.2",
    eventsource: "^4.0.0",
    ws: "^8.0.0"
  },
  peerDependenciesMeta: {
    "@daytonaio/sdk": {
      optional: true
    },
    "@e2b/code-interpreter": {
      optional: true
    },
    dockerode: {
      optional: true
    },
    "drizzle-kit": {
      optional: true
    },
    "drizzle-orm": {
      optional: true
    },
    eventsource: {
      optional: true
    },
    ws: {
      optional: true
    }
  },
  stableVersion: "0.8.0"
};

// src/common/log.ts
import {
  pino,
  stdTimeFunctions
} from "pino";
import { z } from "zod/v4";

// src/common/log-levels.ts
var LogLevels = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  CRITICAL: 5
};

// src/common/logfmt.ts
var LOG_LEVEL_COLORS = {
  [LogLevels.CRITICAL]: "\x1B[31m",
  // Red
  [LogLevels.ERROR]: "\x1B[31m",
  // Red
  [LogLevels.WARN]: "\x1B[33m",
  // Yellow
  [LogLevels.INFO]: "\x1B[32m",
  // Green
  [LogLevels.DEBUG]: "\x1B[36m",
  // Cyan
  [LogLevels.TRACE]: "\x1B[36m"
  // Cyan
};
var RESET_COLOR = "\x1B[0m";
function stringify(data) {
  let line = "";
  const entries = Object.entries(data);
  for (let i = 0; i < entries.length; i++) {
    const [key, valueRaw] = entries[i];
    let isNull = false;
    let valueString;
    if (valueRaw == null) {
      isNull = true;
      valueString = "";
    } else {
      valueString = valueRaw.toString();
    }
    if (valueString.length > 512 && key !== "msg" && key !== "error")
      valueString = `${valueString.slice(0, 512)}...`;
    const needsQuoting = valueString.indexOf(" ") > -1 || valueString.indexOf("=") > -1;
    const needsEscaping = valueString.indexOf('"') > -1 || valueString.indexOf("\\") > -1;
    valueString = valueString.replace(/\n/g, "\\n");
    if (needsEscaping) valueString = valueString.replace(/["\\]/g, "\\$&");
    if (needsQuoting || needsEscaping) valueString = `"${valueString}"`;
    if (valueString === "" && !isNull) valueString = '""';
    if (LOGGER_CONFIG.enableColor) {
      let color = "\x1B[2m";
      if (key === "level") {
        const level = LogLevels[valueString];
        const levelColor = LOG_LEVEL_COLORS[level];
        if (levelColor) {
          color = levelColor;
        }
      } else if (key === "msg") {
        color = "\x1B[32m";
      } else if (key === "trace") {
        color = "\x1B[34m";
      }
      line += `\x1B[0m\x1B[1m${key}\x1B[0m\x1B[2m=\x1B[0m${color}${valueString}${RESET_COLOR}`;
    } else {
      line += `${key}=${valueString}`;
    }
    if (i !== entries.length - 1) {
      line += " ";
    }
  }
  return line;
}
function formatTimestamp(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(date.getUTCMilliseconds()).padStart(3, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}
function castToLogValue(v) {
  if (typeof v === "string" || typeof v === "number" || typeof v === "bigint" || typeof v === "boolean" || v === null || v === void 0) {
    return v;
  }
  if (v instanceof Error) {
    return String(v);
  }
  try {
    return JSON.stringify(v);
  } catch {
    return "[cannot stringify]";
  }
}
var LOGGER_CONFIG = {
  enableColor: false,
  enableSpreadObject: false,
  enableErrorStack: false
};

// src/common/log.ts
var baseLogger;
var configuredLogLevel;
var loggerCache = /* @__PURE__ */ new Map();
var LogLevelSchema = z.enum([
  "trace",
  "debug",
  "info",
  "warn",
  "error",
  "fatal",
  "silent"
]);
function getPinoLevel(logLevel) {
  if (logLevel) {
    return logLevel;
  }
  if (configuredLogLevel) {
    return configuredLogLevel;
  }
  const raw = (getLogLevel() || "warn").toString().toLowerCase();
  const parsed = LogLevelSchema.safeParse(raw);
  if (parsed.success) {
    return parsed.data;
  }
  return "info";
}
function getIncludeTarget() {
  return getLogTarget();
}
function customWrite(level, o) {
  const entries = {};
  if (getLogTimestamp() && o.time) {
    const date = typeof o.time === "number" ? new Date(o.time) : /* @__PURE__ */ new Date();
    entries.ts = formatTimestamp(date);
  }
  entries.level = level.toUpperCase();
  if (o.target) {
    entries.target = o.target;
  }
  if (o.msg) {
    entries.msg = o.msg;
  }
  for (const [key, value] of Object.entries(o)) {
    if (key !== "time" && key !== "level" && key !== "target" && key !== "msg" && key !== "pid" && key !== "hostname") {
      entries[key] = castToLogValue(value);
    }
  }
  const output = stringify(entries);
  console.log(output);
}
function configureDefaultLogger(logLevel) {
  if (logLevel) {
    configuredLogLevel = logLevel;
  }
  baseLogger = pino({
    level: getPinoLevel(logLevel),
    messageKey: "msg",
    // Do not include pid/hostname in output
    base: {},
    // Keep a string level in the output
    formatters: {
      level(_label, number) {
        return { level: number };
      }
    },
    timestamp: getLogTimestamp() ? stdTimeFunctions.epochTime : false,
    browser: {
      write: {
        fatal: customWrite.bind(null, "fatal"),
        error: customWrite.bind(null, "error"),
        warn: customWrite.bind(null, "warn"),
        info: customWrite.bind(null, "info"),
        debug: customWrite.bind(null, "debug"),
        trace: customWrite.bind(null, "trace")
      }
    },
    hooks: {
      logMethod(inputArgs, method, level) {
        const levelMap = {
          10: "trace",
          20: "debug",
          30: "info",
          40: "warn",
          50: "error",
          60: "fatal"
        };
        const levelName = levelMap[level] || "info";
        const time = getLogTimestamp() ? Date.now() : void 0;
        const bindings = this.bindings?.() || {};
        if (inputArgs.length >= 2) {
          const [objOrMsg, msg] = inputArgs;
          if (typeof objOrMsg === "object" && objOrMsg !== null) {
            customWrite(levelName, {
              ...bindings,
              ...objOrMsg,
              msg,
              time
            });
          } else {
            customWrite(levelName, {
              ...bindings,
              msg: String(objOrMsg),
              time
            });
          }
        } else if (inputArgs.length === 1) {
          const [objOrMsg] = inputArgs;
          if (typeof objOrMsg === "object" && objOrMsg !== null) {
            customWrite(levelName, {
              ...bindings,
              ...objOrMsg,
              time
            });
          } else {
            customWrite(levelName, {
              ...bindings,
              msg: String(objOrMsg),
              time
            });
          }
        }
      }
    }
  });
  loggerCache.clear();
}
function getBaseLogger() {
  if (!baseLogger) {
    configureDefaultLogger();
  }
  return baseLogger;
}
function getLogger(name = "default") {
  const cached = loggerCache.get(name);
  if (cached) {
    return cached;
  }
  const base = getBaseLogger();
  const child = getIncludeTarget() ? base.child({ target: name }) : base;
  loggerCache.set(name, child);
  return child;
}

// src/utils.ts
var VERSION2 = package_default.version;
var _userAgent;
function httpUserAgent() {
  if (_userAgent !== void 0) {
    return _userAgent;
  }
  let userAgent = `RivetKit/${VERSION2}`;
  const navigatorObj = typeof navigator !== "undefined" ? navigator : void 0;
  if (navigatorObj?.userAgent) userAgent += ` ${navigatorObj.userAgent}`;
  _userAgent = userAgent;
  return userAgent;
}
function getEnvUniversal(key) {
  if (typeof Deno !== "undefined") {
    return Deno.env.get(key);
  } else if (typeof process !== "undefined") {
    return process.env[key];
  }
}
function promiseWithResolvers(onReject) {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  promise.catch(onReject);
  return { promise, resolve, reject };
}
function bufferToArrayBuffer(buf) {
  return buf.buffer.slice(
    buf.byteOffset,
    buf.byteOffset + buf.byteLength
  );
}
function combineUrlPath(endpoint, path, queryParams) {
  const baseUrl = new URL(endpoint);
  const pathParts = path.split("?");
  const pathOnly = pathParts[0];
  const existingQuery = pathParts[1] || "";
  const basePath = baseUrl.pathname.replace(/\/$/, "");
  const cleanPath = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
  const fullPath = (basePath + cleanPath).replace(/\/\//g, "/");
  const queryParts = [];
  if (existingQuery) {
    queryParts.push(existingQuery);
  }
  if (queryParams) {
    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== void 0) {
        queryParts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        );
      }
    }
  }
  const fullQuery = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  return `${baseUrl.protocol}//${baseUrl.host}${fullPath}${fullQuery}`;
}
var EXTRA_ERROR_LOG = {
  issues: "https://github.com/rivet-dev/rivetkit/issues",
  support: "https://rivet.dev/discord",
  version: VERSION2
};

// src/devtools-loader/log.ts
function logger() {
  return getLogger("devtools");
}

// src/devtools-loader/index.ts
var DEFAULT_DEVTOOLS_URL = (version = VERSION2) => `https://releases.rivet.dev/rivet/latest/devtools/mod.js?v=${version}`;
var scriptId = "rivetkit-devtools-script";
function injectDevtools(config4) {
  if (!window) {
    logger().warn("devtools not available outside browser environment");
    return;
  }
  if (!document.getElementById(scriptId)) {
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = DEFAULT_DEVTOOLS_URL();
    script.async = true;
    document.head.appendChild(script);
  }
  window.__rivetkit = window.__rivetkit || [];
  window.__rivetkit.push(config4);
}

// src/remote-manager-driver/mod.ts
import * as cbor3 from "cbor-x";
import invariant3 from "invariant";

// src/actor/keys.ts
var EMPTY_KEY = "/";
var KEY_SEPARATOR = "/";
function serializeActorKey(key) {
  if (key.length === 0) {
    return EMPTY_KEY;
  }
  const escapedParts = key.map((part) => {
    if (part === "") {
      return "\\0";
    }
    let escaped = part.replace(/\\/g, "\\\\");
    escaped = escaped.replace(/\//g, `\\${KEY_SEPARATOR}`);
    return escaped;
  });
  return escapedParts.join(KEY_SEPARATOR);
}
function deserializeActorKey(keyString) {
  if (keyString === void 0 || keyString === null || keyString === EMPTY_KEY) {
    return [];
  }
  const parts = [];
  let currentPart = "";
  let escaping = false;
  let isEmptyStringMarker = false;
  for (let i = 0; i < keyString.length; i++) {
    const char = keyString[i];
    if (escaping) {
      if (char === "0") {
        isEmptyStringMarker = true;
      } else {
        currentPart += char;
      }
      escaping = false;
    } else if (char === "\\") {
      escaping = true;
    } else if (char === KEY_SEPARATOR) {
      if (isEmptyStringMarker) {
        parts.push("");
        isEmptyStringMarker = false;
      } else {
        parts.push(currentPart);
      }
      currentPart = "";
    } else {
      currentPart += char;
    }
  }
  if (escaping) {
    parts.push(currentPart + "\\");
  } else if (isEmptyStringMarker) {
    parts.push("");
  } else if (currentPart !== "" || parts.length > 0) {
    parts.push(currentPart);
  }
  return parts;
}

// src/serde.ts
import * as cbor from "cbor-x";
import invariant from "invariant";

// src/actor/protocol/serde.ts
import { z as z2 } from "zod/v4";

// src/actor/log.ts
function loggerWithoutContext() {
  return getLogger("actor-runtime");
}

// src/actor/utils.ts
function assertUnreachable2(x) {
  loggerWithoutContext().error({
    msg: "unreachable",
    value: `${x}`,
    stack: new Error().stack
  });
  throw new Unreachable(x);
}

// src/actor/protocol/serde.ts
var EncodingSchema = z2.enum(["json", "cbor", "bare"]);
function base64EncodeUint8Array(uint8Array) {
  let binary = "";
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}
function base64EncodeArrayBuffer(arrayBuffer) {
  const uint8Array = new Uint8Array(arrayBuffer);
  return base64EncodeUint8Array(uint8Array);
}
function base64DecodeToUint8Array(base64) {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(base64, "base64"));
  }
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
function base64DecodeToArrayBuffer(base64) {
  return base64DecodeToUint8Array(base64).buffer;
}
function jsonStringifyCompat(input) {
  return JSON.stringify(input, (_key, value) => {
    if (typeof value === "bigint") {
      return ["$BigInt", value.toString()];
    } else if (value instanceof ArrayBuffer) {
      return ["$ArrayBuffer", base64EncodeArrayBuffer(value)];
    } else if (value instanceof Uint8Array) {
      return ["$Uint8Array", base64EncodeUint8Array(value)];
    }
    if (Array.isArray(value) && value.length === 2 && typeof value[0] === "string" && value[0].startsWith("$")) {
      return ["$" + value[0], value[1]];
    }
    return value;
  });
}
function jsonParseCompat(input) {
  return JSON.parse(input, (_key, value) => {
    if (Array.isArray(value) && value.length === 2 && typeof value[0] === "string" && value[0].startsWith("$")) {
      if (value[0] === "$BigInt") {
        return BigInt(value[1]);
      } else if (value[0] === "$ArrayBuffer") {
        return base64DecodeToArrayBuffer(value[1]);
      } else if (value[0] === "$Uint8Array") {
        return base64DecodeToUint8Array(value[1]);
      }
      if (value[0].startsWith("$$")) {
        return [value[0].substring(1), value[1]];
      }
      throw new Error(
        `Unknown JSON encoding type: ${value[0]}. This may indicate corrupted data or a version mismatch.`
      );
    }
    return value;
  });
}

// src/serde.ts
function uint8ArrayToBase64(uint8Array) {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(uint8Array).toString("base64");
  }
  let binary = "";
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}
function contentTypeForEncoding(encoding) {
  if (encoding === "json") {
    return "application/json";
  } else if (encoding === "cbor" || encoding === "bare") {
    return "application/octet-stream";
  } else {
    assertUnreachable(encoding);
  }
}
function serializeWithEncoding(encoding, value, versionedDataHandler, version, zodSchema, toJson, toBare) {
  if (encoding === "json") {
    const jsonValue = toJson(value);
    const validated = zodSchema.parse(jsonValue);
    return jsonStringifyCompat(validated);
  } else if (encoding === "cbor") {
    const jsonValue = toJson(value);
    const validated = zodSchema.parse(jsonValue);
    return cbor.encode(validated);
  } else if (encoding === "bare") {
    if (!versionedDataHandler) {
      throw new Error(
        "VersionedDataHandler is required for 'bare' encoding"
      );
    }
    if (version === void 0) {
      throw new Error("version is required for 'bare' encoding");
    }
    const bareValue = toBare(value);
    return versionedDataHandler.serializeWithEmbeddedVersion(
      bareValue,
      version
    );
  } else {
    assertUnreachable(encoding);
  }
}
function deserializeWithEncoding(encoding, buffer, versionedDataHandler, zodSchema, fromJson, fromBare) {
  if (encoding === "json") {
    let parsed;
    if (typeof buffer === "string") {
      parsed = jsonParseCompat(buffer);
    } else {
      const decoder = new TextDecoder("utf-8");
      const jsonString = decoder.decode(buffer);
      parsed = jsonParseCompat(jsonString);
    }
    const validated = zodSchema.parse(parsed);
    return fromJson(validated);
  } else if (encoding === "cbor") {
    invariant(
      typeof buffer !== "string",
      "buffer cannot be string for cbor encoding"
    );
    const decoded = cbor.decode(buffer);
    const validated = zodSchema.parse(decoded);
    return fromJson(validated);
  } else if (encoding === "bare") {
    invariant(
      typeof buffer !== "string",
      "buffer cannot be string for bare encoding"
    );
    if (!versionedDataHandler) {
      throw new Error(
        "VersionedDataHandler is required for 'bare' encoding"
      );
    }
    const bareValue = versionedDataHandler.deserializeWithEmbeddedVersion(buffer);
    return fromBare(bareValue);
  } else {
    assertUnreachable(encoding);
  }
}

// src/common/actor-router-consts.ts
var PATH_CONNECT = "/connect";
var PATH_WEBSOCKET_PREFIX = "/websocket/";
var HEADER_ENCODING = "x-rivet-encoding";
var HEADER_CONN_PARAMS = "x-rivet-conn-params";
var HEADER_RIVET_TOKEN = "x-rivet-token";
var WS_PROTOCOL_STANDARD = "rivet";
var WS_PROTOCOL_ENCODING = "rivet_encoding.";
var WS_PROTOCOL_CONN_PARAMS = "rivet_conn_params.";

// src/client/log.ts
function logger2() {
  return getLogger("actor-client");
}

// src/common/websocket.ts
var webSocketPromise = null;
async function importWebSocket() {
  if (webSocketPromise !== null) {
    return webSocketPromise;
  }
  webSocketPromise = (async () => {
    let _WebSocket;
    if (typeof WebSocket !== "undefined") {
      _WebSocket = WebSocket;
    } else {
      try {
        const moduleName = "ws";
        const ws = await import(
          /* webpackIgnore: true */
          moduleName
        );
        _WebSocket = ws.default;
        logger2().debug("using websocket from npm");
      } catch {
        _WebSocket = class MockWebSocket {
          constructor() {
            throw new Error(
              'WebSocket support requires installing the "ws" peer dependency.'
            );
          }
        };
        logger2().debug("using mock websocket");
      }
    }
    return _WebSocket;
  })();
  return webSocketPromise;
}

// src/remote-manager-driver/api-utils.ts
import { z as z4 } from "zod/v4";

// src/client/utils.ts
import * as cbor2 from "cbor-x";
import invariant2 from "invariant";

// src/schemas/client-protocol/versioned.ts
import { createVersionedDataHandler } from "vbare";

// dist/schemas/client-protocol/v1.ts
import * as bare from "@rivetkit/bare-ts";
var config = /* @__PURE__ */ bare.Config({});
function readInit(bc) {
  return {
    actorId: bare.readString(bc),
    connectionId: bare.readString(bc),
    connectionToken: bare.readString(bc)
  };
}
function writeInit(bc, x) {
  bare.writeString(bc, x.actorId);
  bare.writeString(bc, x.connectionId);
  bare.writeString(bc, x.connectionToken);
}
function read0(bc) {
  return bare.readBool(bc) ? bare.readData(bc) : null;
}
function write0(bc, x) {
  bare.writeBool(bc, x !== null);
  if (x !== null) {
    bare.writeData(bc, x);
  }
}
function read1(bc) {
  return bare.readBool(bc) ? bare.readUint(bc) : null;
}
function write1(bc, x) {
  bare.writeBool(bc, x !== null);
  if (x !== null) {
    bare.writeUint(bc, x);
  }
}
function readError(bc) {
  return {
    group: bare.readString(bc),
    code: bare.readString(bc),
    message: bare.readString(bc),
    metadata: read0(bc),
    actionId: read1(bc)
  };
}
function writeError(bc, x) {
  bare.writeString(bc, x.group);
  bare.writeString(bc, x.code);
  bare.writeString(bc, x.message);
  write0(bc, x.metadata);
  write1(bc, x.actionId);
}
function readActionResponse(bc) {
  return {
    id: bare.readUint(bc),
    output: bare.readData(bc)
  };
}
function writeActionResponse(bc, x) {
  bare.writeUint(bc, x.id);
  bare.writeData(bc, x.output);
}
function readEvent(bc) {
  return {
    name: bare.readString(bc),
    args: bare.readData(bc)
  };
}
function writeEvent(bc, x) {
  bare.writeString(bc, x.name);
  bare.writeData(bc, x.args);
}
function readToClientBody(bc) {
  const offset = bc.offset;
  const tag = bare.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "Init", val: readInit(bc) };
    case 1:
      return { tag: "Error", val: readError(bc) };
    case 2:
      return { tag: "ActionResponse", val: readActionResponse(bc) };
    case 3:
      return { tag: "Event", val: readEvent(bc) };
    default: {
      bc.offset = offset;
      throw new bare.BareError(offset, "invalid tag");
    }
  }
}
function writeToClientBody(bc, x) {
  switch (x.tag) {
    case "Init": {
      bare.writeU8(bc, 0);
      writeInit(bc, x.val);
      break;
    }
    case "Error": {
      bare.writeU8(bc, 1);
      writeError(bc, x.val);
      break;
    }
    case "ActionResponse": {
      bare.writeU8(bc, 2);
      writeActionResponse(bc, x.val);
      break;
    }
    case "Event": {
      bare.writeU8(bc, 3);
      writeEvent(bc, x.val);
      break;
    }
  }
}
function readToClient(bc) {
  return {
    body: readToClientBody(bc)
  };
}
function writeToClient(bc, x) {
  writeToClientBody(bc, x.body);
}
function encodeToClient(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeToClient(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToClient(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readToClient(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readActionRequest(bc) {
  return {
    id: bare.readUint(bc),
    name: bare.readString(bc),
    args: bare.readData(bc)
  };
}
function writeActionRequest(bc, x) {
  bare.writeUint(bc, x.id);
  bare.writeString(bc, x.name);
  bare.writeData(bc, x.args);
}
function readSubscriptionRequest(bc) {
  return {
    eventName: bare.readString(bc),
    subscribe: bare.readBool(bc)
  };
}
function writeSubscriptionRequest(bc, x) {
  bare.writeString(bc, x.eventName);
  bare.writeBool(bc, x.subscribe);
}
function readToServerBody(bc) {
  const offset = bc.offset;
  const tag = bare.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "ActionRequest", val: readActionRequest(bc) };
    case 1:
      return { tag: "SubscriptionRequest", val: readSubscriptionRequest(bc) };
    default: {
      bc.offset = offset;
      throw new bare.BareError(offset, "invalid tag");
    }
  }
}
function writeToServerBody(bc, x) {
  switch (x.tag) {
    case "ActionRequest": {
      bare.writeU8(bc, 0);
      writeActionRequest(bc, x.val);
      break;
    }
    case "SubscriptionRequest": {
      bare.writeU8(bc, 1);
      writeSubscriptionRequest(bc, x.val);
      break;
    }
  }
}
function readToServer(bc) {
  return {
    body: readToServerBody(bc)
  };
}
function writeToServer(bc, x) {
  writeToServerBody(bc, x.body);
}
function encodeToServer(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeToServer(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToServer(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readToServer(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionRequest(bc) {
  return {
    args: bare.readData(bc)
  };
}
function writeHttpActionRequest(bc, x) {
  bare.writeData(bc, x.args);
}
function encodeHttpActionRequest(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpActionRequest(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionRequest(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpActionRequest(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionResponse(bc) {
  return {
    output: bare.readData(bc)
  };
}
function writeHttpActionResponse(bc, x) {
  bare.writeData(bc, x.output);
}
function encodeHttpActionResponse(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpActionResponse(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionResponse(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpActionResponse(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResponseError(bc) {
  return {
    group: bare.readString(bc),
    code: bare.readString(bc),
    message: bare.readString(bc),
    metadata: read0(bc)
  };
}
function writeHttpResponseError(bc, x) {
  bare.writeString(bc, x.group);
  bare.writeString(bc, x.code);
  bare.writeString(bc, x.message);
  write0(bc, x.metadata);
}
function encodeHttpResponseError(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpResponseError(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResponseError(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpResponseError(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResolveResponse(bc) {
  return {
    actorId: bare.readString(bc)
  };
}
function writeHttpResolveResponse(bc, x) {
  bare.writeString(bc, x.actorId);
}
function encodeHttpResolveResponse(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpResolveResponse(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResolveResponse(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpResolveResponse(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// dist/schemas/client-protocol/v2.ts
import * as bare2 from "@rivetkit/bare-ts";
var config2 = /* @__PURE__ */ bare2.Config({});
function readInit2(bc) {
  return {
    actorId: bare2.readString(bc),
    connectionId: bare2.readString(bc)
  };
}
function writeInit2(bc, x) {
  bare2.writeString(bc, x.actorId);
  bare2.writeString(bc, x.connectionId);
}
function read02(bc) {
  return bare2.readBool(bc) ? bare2.readData(bc) : null;
}
function write02(bc, x) {
  bare2.writeBool(bc, x !== null);
  if (x !== null) {
    bare2.writeData(bc, x);
  }
}
function read12(bc) {
  return bare2.readBool(bc) ? bare2.readUint(bc) : null;
}
function write12(bc, x) {
  bare2.writeBool(bc, x !== null);
  if (x !== null) {
    bare2.writeUint(bc, x);
  }
}
function readError2(bc) {
  return {
    group: bare2.readString(bc),
    code: bare2.readString(bc),
    message: bare2.readString(bc),
    metadata: read02(bc),
    actionId: read12(bc)
  };
}
function writeError2(bc, x) {
  bare2.writeString(bc, x.group);
  bare2.writeString(bc, x.code);
  bare2.writeString(bc, x.message);
  write02(bc, x.metadata);
  write12(bc, x.actionId);
}
function readActionResponse2(bc) {
  return {
    id: bare2.readUint(bc),
    output: bare2.readData(bc)
  };
}
function writeActionResponse2(bc, x) {
  bare2.writeUint(bc, x.id);
  bare2.writeData(bc, x.output);
}
function readEvent2(bc) {
  return {
    name: bare2.readString(bc),
    args: bare2.readData(bc)
  };
}
function writeEvent2(bc, x) {
  bare2.writeString(bc, x.name);
  bare2.writeData(bc, x.args);
}
function readToClientBody2(bc) {
  const offset = bc.offset;
  const tag = bare2.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "Init", val: readInit2(bc) };
    case 1:
      return { tag: "Error", val: readError2(bc) };
    case 2:
      return { tag: "ActionResponse", val: readActionResponse2(bc) };
    case 3:
      return { tag: "Event", val: readEvent2(bc) };
    default: {
      bc.offset = offset;
      throw new bare2.BareError(offset, "invalid tag");
    }
  }
}
function writeToClientBody2(bc, x) {
  switch (x.tag) {
    case "Init": {
      bare2.writeU8(bc, 0);
      writeInit2(bc, x.val);
      break;
    }
    case "Error": {
      bare2.writeU8(bc, 1);
      writeError2(bc, x.val);
      break;
    }
    case "ActionResponse": {
      bare2.writeU8(bc, 2);
      writeActionResponse2(bc, x.val);
      break;
    }
    case "Event": {
      bare2.writeU8(bc, 3);
      writeEvent2(bc, x.val);
      break;
    }
  }
}
function readToClient2(bc) {
  return {
    body: readToClientBody2(bc)
  };
}
function writeToClient2(bc, x) {
  writeToClientBody2(bc, x.body);
}
function encodeToClient2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeToClient2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToClient2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readToClient2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readActionRequest2(bc) {
  return {
    id: bare2.readUint(bc),
    name: bare2.readString(bc),
    args: bare2.readData(bc)
  };
}
function writeActionRequest2(bc, x) {
  bare2.writeUint(bc, x.id);
  bare2.writeString(bc, x.name);
  bare2.writeData(bc, x.args);
}
function readSubscriptionRequest2(bc) {
  return {
    eventName: bare2.readString(bc),
    subscribe: bare2.readBool(bc)
  };
}
function writeSubscriptionRequest2(bc, x) {
  bare2.writeString(bc, x.eventName);
  bare2.writeBool(bc, x.subscribe);
}
function readToServerBody2(bc) {
  const offset = bc.offset;
  const tag = bare2.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "ActionRequest", val: readActionRequest2(bc) };
    case 1:
      return { tag: "SubscriptionRequest", val: readSubscriptionRequest2(bc) };
    default: {
      bc.offset = offset;
      throw new bare2.BareError(offset, "invalid tag");
    }
  }
}
function writeToServerBody2(bc, x) {
  switch (x.tag) {
    case "ActionRequest": {
      bare2.writeU8(bc, 0);
      writeActionRequest2(bc, x.val);
      break;
    }
    case "SubscriptionRequest": {
      bare2.writeU8(bc, 1);
      writeSubscriptionRequest2(bc, x.val);
      break;
    }
  }
}
function readToServer2(bc) {
  return {
    body: readToServerBody2(bc)
  };
}
function writeToServer2(bc, x) {
  writeToServerBody2(bc, x.body);
}
function encodeToServer2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeToServer2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToServer2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readToServer2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionRequest2(bc) {
  return {
    args: bare2.readData(bc)
  };
}
function writeHttpActionRequest2(bc, x) {
  bare2.writeData(bc, x.args);
}
function encodeHttpActionRequest2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpActionRequest2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionRequest2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpActionRequest2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionResponse2(bc) {
  return {
    output: bare2.readData(bc)
  };
}
function writeHttpActionResponse2(bc, x) {
  bare2.writeData(bc, x.output);
}
function encodeHttpActionResponse2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpActionResponse2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionResponse2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpActionResponse2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResponseError2(bc) {
  return {
    group: bare2.readString(bc),
    code: bare2.readString(bc),
    message: bare2.readString(bc),
    metadata: read02(bc)
  };
}
function writeHttpResponseError2(bc, x) {
  bare2.writeString(bc, x.group);
  bare2.writeString(bc, x.code);
  bare2.writeString(bc, x.message);
  write02(bc, x.metadata);
}
function encodeHttpResponseError2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpResponseError2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResponseError2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpResponseError2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResolveResponse2(bc) {
  return {
    actorId: bare2.readString(bc)
  };
}
function writeHttpResolveResponse2(bc, x) {
  bare2.writeString(bc, x.actorId);
}
function encodeHttpResolveResponse2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpResolveResponse2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResolveResponse2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpResolveResponse2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// dist/schemas/client-protocol/v3.ts
import * as bare3 from "@rivetkit/bare-ts";
var config3 = /* @__PURE__ */ bare3.Config({});
function readInit3(bc) {
  return {
    actorId: bare3.readString(bc),
    connectionId: bare3.readString(bc)
  };
}
function writeInit3(bc, x) {
  bare3.writeString(bc, x.actorId);
  bare3.writeString(bc, x.connectionId);
}
function read03(bc) {
  return bare3.readBool(bc) ? bare3.readData(bc) : null;
}
function write03(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeData(bc, x);
  }
}
function read13(bc) {
  return bare3.readBool(bc) ? bare3.readUint(bc) : null;
}
function write13(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeUint(bc, x);
  }
}
function readError3(bc) {
  return {
    group: bare3.readString(bc),
    code: bare3.readString(bc),
    message: bare3.readString(bc),
    metadata: read03(bc),
    actionId: read13(bc)
  };
}
function writeError3(bc, x) {
  bare3.writeString(bc, x.group);
  bare3.writeString(bc, x.code);
  bare3.writeString(bc, x.message);
  write03(bc, x.metadata);
  write13(bc, x.actionId);
}
function readActionResponse3(bc) {
  return {
    id: bare3.readUint(bc),
    output: bare3.readData(bc)
  };
}
function writeActionResponse3(bc, x) {
  bare3.writeUint(bc, x.id);
  bare3.writeData(bc, x.output);
}
function readEvent3(bc) {
  return {
    name: bare3.readString(bc),
    args: bare3.readData(bc)
  };
}
function writeEvent3(bc, x) {
  bare3.writeString(bc, x.name);
  bare3.writeData(bc, x.args);
}
function readToClientBody3(bc) {
  const offset = bc.offset;
  const tag = bare3.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "Init", val: readInit3(bc) };
    case 1:
      return { tag: "Error", val: readError3(bc) };
    case 2:
      return { tag: "ActionResponse", val: readActionResponse3(bc) };
    case 3:
      return { tag: "Event", val: readEvent3(bc) };
    default: {
      bc.offset = offset;
      throw new bare3.BareError(offset, "invalid tag");
    }
  }
}
function writeToClientBody3(bc, x) {
  switch (x.tag) {
    case "Init": {
      bare3.writeU8(bc, 0);
      writeInit3(bc, x.val);
      break;
    }
    case "Error": {
      bare3.writeU8(bc, 1);
      writeError3(bc, x.val);
      break;
    }
    case "ActionResponse": {
      bare3.writeU8(bc, 2);
      writeActionResponse3(bc, x.val);
      break;
    }
    case "Event": {
      bare3.writeU8(bc, 3);
      writeEvent3(bc, x.val);
      break;
    }
  }
}
function readToClient3(bc) {
  return {
    body: readToClientBody3(bc)
  };
}
function writeToClient3(bc, x) {
  writeToClientBody3(bc, x.body);
}
function encodeToClient3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeToClient3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToClient3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readToClient3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readActionRequest3(bc) {
  return {
    id: bare3.readUint(bc),
    name: bare3.readString(bc),
    args: bare3.readData(bc)
  };
}
function writeActionRequest3(bc, x) {
  bare3.writeUint(bc, x.id);
  bare3.writeString(bc, x.name);
  bare3.writeData(bc, x.args);
}
function readSubscriptionRequest3(bc) {
  return {
    eventName: bare3.readString(bc),
    subscribe: bare3.readBool(bc)
  };
}
function writeSubscriptionRequest3(bc, x) {
  bare3.writeString(bc, x.eventName);
  bare3.writeBool(bc, x.subscribe);
}
function readToServerBody3(bc) {
  const offset = bc.offset;
  const tag = bare3.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "ActionRequest", val: readActionRequest3(bc) };
    case 1:
      return { tag: "SubscriptionRequest", val: readSubscriptionRequest3(bc) };
    default: {
      bc.offset = offset;
      throw new bare3.BareError(offset, "invalid tag");
    }
  }
}
function writeToServerBody3(bc, x) {
  switch (x.tag) {
    case "ActionRequest": {
      bare3.writeU8(bc, 0);
      writeActionRequest3(bc, x.val);
      break;
    }
    case "SubscriptionRequest": {
      bare3.writeU8(bc, 1);
      writeSubscriptionRequest3(bc, x.val);
      break;
    }
  }
}
function readToServer3(bc) {
  return {
    body: readToServerBody3(bc)
  };
}
function writeToServer3(bc, x) {
  writeToServerBody3(bc, x.body);
}
function encodeToServer3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeToServer3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToServer3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readToServer3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionRequest3(bc) {
  return {
    args: bare3.readData(bc)
  };
}
function writeHttpActionRequest3(bc, x) {
  bare3.writeData(bc, x.args);
}
function encodeHttpActionRequest3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpActionRequest3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionRequest3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpActionRequest3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionResponse3(bc) {
  return {
    output: bare3.readData(bc)
  };
}
function writeHttpActionResponse3(bc, x) {
  bare3.writeData(bc, x.output);
}
function encodeHttpActionResponse3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpActionResponse3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionResponse3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpActionResponse3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function read2(bc) {
  return bare3.readBool(bc) ? bare3.readString(bc) : null;
}
function write2(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeString(bc, x);
  }
}
function read3(bc) {
  return bare3.readBool(bc) ? bare3.readBool(bc) : null;
}
function write3(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeBool(bc, x);
  }
}
function read4(bc) {
  return bare3.readBool(bc) ? bare3.readU64(bc) : null;
}
function write4(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeU64(bc, x);
  }
}
function readHttpQueueSendRequest(bc) {
  return {
    body: bare3.readData(bc),
    name: read2(bc),
    wait: read3(bc),
    timeout: read4(bc)
  };
}
function writeHttpQueueSendRequest(bc, x) {
  bare3.writeData(bc, x.body);
  write2(bc, x.name);
  write3(bc, x.wait);
  write4(bc, x.timeout);
}
function encodeHttpQueueSendRequest(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpQueueSendRequest(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpQueueSendRequest(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpQueueSendRequest(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpQueueSendResponse(bc) {
  return {
    status: bare3.readString(bc),
    response: read03(bc)
  };
}
function writeHttpQueueSendResponse(bc, x) {
  bare3.writeString(bc, x.status);
  write03(bc, x.response);
}
function encodeHttpQueueSendResponse(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpQueueSendResponse(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpQueueSendResponse(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpQueueSendResponse(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResponseError3(bc) {
  return {
    group: bare3.readString(bc),
    code: bare3.readString(bc),
    message: bare3.readString(bc),
    metadata: read03(bc)
  };
}
function writeHttpResponseError3(bc, x) {
  bare3.writeString(bc, x.group);
  bare3.writeString(bc, x.code);
  bare3.writeString(bc, x.message);
  write03(bc, x.metadata);
}
function encodeHttpResponseError3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpResponseError3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResponseError3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpResponseError3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResolveResponse3(bc) {
  return {
    actorId: bare3.readString(bc)
  };
}
function writeHttpResolveResponse3(bc, x) {
  bare3.writeString(bc, x.actorId);
}
function encodeHttpResolveResponse3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpResolveResponse3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResolveResponse3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpResolveResponse3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// src/schemas/client-protocol/versioned.ts
var CURRENT_VERSION = 3;
var v1ToV2 = (v1Data) => {
  if (v1Data.body.tag === "Init") {
    const { actorId, connectionId } = v1Data.body.val;
    return {
      body: {
        tag: "Init",
        val: {
          actorId,
          connectionId
        }
      }
    };
  }
  return v1Data;
};
var v2ToV1 = (v2Data) => {
  if (v2Data.body.tag === "Init") {
    const { actorId, connectionId } = v2Data.body.val;
    return {
      body: {
        tag: "Init",
        val: {
          actorId,
          connectionId,
          connectionToken: ""
          // Add empty connectionToken for v1 compatibility
        }
      }
    };
  }
  return v2Data;
};
var v2ToV3 = (v2Data) => {
  return v2Data;
};
var v3ToV2 = (v3Data) => {
  return v3Data;
};
var v1ToServerV2 = (v1Data) => {
  return v1Data;
};
var v2ToServerV3 = (v2Data) => {
  return v2Data;
};
var v3ToServerV2 = (v3Data) => {
  return v3Data;
};
var v2ToServerV1 = (v2Data) => {
  return v2Data;
};
var TO_SERVER_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeToServer(bytes);
      case 2:
        return decodeToServer2(bytes);
      case 3:
        return decodeToServer3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeToServer(data);
      case 2:
        return encodeToServer2(data);
      case 3:
        return encodeToServer3(data);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [v1ToServerV2, v2ToServerV3],
  serializeConverters: () => [v3ToServerV2, v2ToServerV1]
});
var TO_CLIENT_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeToClient(bytes);
      case 2:
        return decodeToClient2(bytes);
      case 3:
        return decodeToClient3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeToClient(data);
      case 2:
        return encodeToClient2(data);
      case 3:
        return encodeToClient3(data);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [v1ToV2, v2ToV3],
  serializeConverters: () => [v3ToV2, v2ToV1]
});
var HTTP_ACTION_REQUEST_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpActionRequest(bytes);
      case 2:
        return decodeHttpActionRequest2(bytes);
      case 3:
        return decodeHttpActionRequest3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpActionRequest(
          data
        );
      case 2:
        return encodeHttpActionRequest2(
          data
        );
      case 3:
        return encodeHttpActionRequest3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_ACTION_RESPONSE_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpActionResponse(bytes);
      case 2:
        return decodeHttpActionResponse2(bytes);
      case 3:
        return decodeHttpActionResponse3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpActionResponse(
          data
        );
      case 2:
        return encodeHttpActionResponse2(
          data
        );
      case 3:
        return encodeHttpActionResponse3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_QUEUE_SEND_REQUEST_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 3:
        return decodeHttpQueueSendRequest(bytes);
      default:
        throw new Error(
          `HttpQueueSendRequest only exists in version 3+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 3:
        return encodeHttpQueueSendRequest(
          data
        );
      default:
        throw new Error(
          `HttpQueueSendRequest only exists in version 3+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_QUEUE_SEND_RESPONSE_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 3:
        return decodeHttpQueueSendResponse(bytes);
      default:
        throw new Error(
          `HttpQueueSendResponse only exists in version 3+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 3:
        return encodeHttpQueueSendResponse(
          data
        );
      default:
        throw new Error(
          `HttpQueueSendResponse only exists in version 3+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_RESPONSE_ERROR_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpResponseError(bytes);
      case 2:
        return decodeHttpResponseError2(bytes);
      case 3:
        return decodeHttpResponseError3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpResponseError(
          data
        );
      case 2:
        return encodeHttpResponseError2(
          data
        );
      case 3:
        return encodeHttpResponseError3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_RESOLVE_RESPONSE_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpResolveResponse(bytes);
      case 2:
        return decodeHttpResolveResponse2(bytes);
      case 3:
        return decodeHttpResolveResponse3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpResolveResponse(
          data
        );
      case 2:
        return encodeHttpResolveResponse2(
          data
        );
      case 3:
        return encodeHttpResolveResponse3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});

// src/schemas/client-protocol-zod/mod.ts
import { z as z3 } from "zod/v4";
var UintSchema = z3.bigint();
var OptionalUintSchema = UintSchema.nullable();
var InitSchema = z3.object({
  actorId: z3.string(),
  connectionId: z3.string()
});
var ErrorSchema = z3.object({
  group: z3.string(),
  code: z3.string(),
  message: z3.string(),
  metadata: z3.unknown().optional(),
  actionId: OptionalUintSchema
});
var ActionResponseSchema = z3.object({
  id: UintSchema,
  output: z3.unknown()
});
var EventSchema = z3.object({
  name: z3.string(),
  args: z3.unknown()
});
var ToClientBodySchema = z3.discriminatedUnion("tag", [
  z3.object({ tag: z3.literal("Init"), val: InitSchema }),
  z3.object({ tag: z3.literal("Error"), val: ErrorSchema }),
  z3.object({ tag: z3.literal("ActionResponse"), val: ActionResponseSchema }),
  z3.object({ tag: z3.literal("Event"), val: EventSchema })
]);
var ToClientSchema = z3.object({
  body: ToClientBodySchema
});
var ActionRequestSchema = z3.object({
  id: UintSchema,
  name: z3.string(),
  args: z3.unknown()
});
var SubscriptionRequestSchema = z3.object({
  eventName: z3.string(),
  subscribe: z3.boolean()
});
var ToServerBodySchema = z3.discriminatedUnion("tag", [
  z3.object({ tag: z3.literal("ActionRequest"), val: ActionRequestSchema }),
  z3.object({
    tag: z3.literal("SubscriptionRequest"),
    val: SubscriptionRequestSchema
  })
]);
var ToServerSchema = z3.object({
  body: ToServerBodySchema
});
var HttpActionRequestSchema = z3.object({
  args: z3.unknown()
});
var HttpActionResponseSchema = z3.object({
  output: z3.unknown()
});
var HttpQueueSendRequestSchema = z3.object({
  body: z3.unknown(),
  name: z3.string().optional(),
  wait: z3.boolean().optional(),
  timeout: z3.number().optional()
});
var HttpQueueSendResponseSchema = z3.object({
  status: z3.enum(["completed", "timedOut"]),
  response: z3.unknown().optional()
});
var HttpResponseErrorSchema = z3.object({
  group: z3.string(),
  code: z3.string(),
  message: z3.string(),
  metadata: z3.unknown().optional()
});
var HttpResolveRequestSchema = z3.null();
var HttpResolveResponseSchema = z3.object({
  actorId: z3.string()
});

// src/client/errors.ts
var ActorClientError = class extends Error {
};
var InternalError2 = class extends ActorClientError {
};
var ManagerError = class extends ActorClientError {
  constructor(error, opts) {
    super(`Manager error: ${error}`, opts);
  }
};
var MalformedResponseMessage = class extends ActorClientError {
  constructor(cause) {
    super(`Malformed response message: ${cause}`, { cause });
  }
};
var ActorError2 = class extends ActorClientError {
  constructor(group, code, message, metadata) {
    super(message);
    this.group = group;
    this.code = code;
    this.metadata = metadata;
  }
  __type = "ActorError";
};
var HttpRequestError = class extends ActorClientError {
  constructor(message, opts) {
    super(`HTTP request error: ${message}`, { cause: opts?.cause });
  }
};
var ActorConnDisposed = class extends ActorClientError {
  constructor() {
    super("Attempting to interact with a disposed actor connection.");
  }
};
function isSchedulingError(group, code) {
  return group === "guard" && (code === "actor_ready_timeout" || code === "actor_runner_failed");
}
var ActorSchedulingError = class extends ActorError2 {
  actorId;
  details;
  constructor(group, code, actorId, details) {
    super(
      group,
      code,
      `Actor failed to start (${actorId}): ${JSON.stringify(details)}`,
      { actorId, details }
    );
    this.name = "ActorSchedulingError";
    this.actorId = actorId;
    this.details = details;
  }
};

// src/client/utils.ts
function parseWebSocketCloseReason(reason) {
  const [mainPart, rayId] = reason.split("#");
  const [group, code] = mainPart.split(".");
  if (!group || !code) {
    logger2().warn({ msg: "failed to parse close reason", reason });
    return void 0;
  }
  return {
    group,
    code,
    rayId
  };
}
function messageLength(message) {
  if (message instanceof Blob) {
    return message.size;
  }
  if (message instanceof ArrayBuffer) {
    return message.byteLength;
  }
  if (message instanceof Uint8Array) {
    return message.byteLength;
  }
  if (typeof message === "string") {
    return message.length;
  }
  assertUnreachable(message);
}
async function sendHttpRequest(opts) {
  logger2().debug({
    msg: "sending http request",
    url: opts.url,
    encoding: opts.encoding
  });
  let contentType;
  let bodyData;
  if (opts.method === "POST" || opts.method === "PUT") {
    invariant2(opts.body !== void 0, "missing body");
    contentType = contentTypeForEncoding(opts.encoding);
    bodyData = serializeWithEncoding(
      opts.encoding,
      opts.body,
      opts.requestVersionedDataHandler,
      opts.requestVersion,
      opts.requestZodSchema,
      opts.requestToJson,
      opts.requestToBare
    );
  }
  let response;
  try {
    response = await (opts.customFetch ?? fetch)(
      new globalThis.Request(opts.url, {
        method: opts.method,
        headers: {
          ...opts.headers,
          ...contentType ? {
            "Content-Type": contentType
          } : {},
          "User-Agent": httpUserAgent()
        },
        body: bodyData,
        credentials: "include",
        signal: opts.signal
      })
    );
  } catch (error) {
    throw new HttpRequestError(`Request failed: ${error}`, {
      cause: error
    });
  }
  if (!response.ok) {
    const bufferResponse = await response.arrayBuffer();
    const contentType2 = response.headers.get("content-type");
    const rayId = response.headers.get("x-rivet-ray-id");
    const encoding = contentType2?.includes("application/json") ? "json" : opts.encoding;
    try {
      const responseData = deserializeWithEncoding(
        encoding,
        new Uint8Array(bufferResponse),
        HTTP_RESPONSE_ERROR_VERSIONED,
        HttpResponseErrorSchema,
        // JSON: metadata is already unknown
        (json) => json,
        // BARE: decode ArrayBuffer metadata to unknown
        (bare4) => ({
          group: bare4.group,
          code: bare4.code,
          message: bare4.message,
          metadata: bare4.metadata ? cbor2.decode(new Uint8Array(bare4.metadata)) : void 0
        })
      );
      throw new ActorError2(
        responseData.group,
        responseData.code,
        responseData.message,
        responseData.metadata
      );
    } catch (error) {
      if (error instanceof ActorError2) {
        throw error;
      }
      const textResponse = new TextDecoder("utf-8", {
        fatal: false
      }).decode(bufferResponse);
      if (rayId) {
        throw new HttpRequestError(
          `${response.statusText} (${response.status}) (Ray ID: ${rayId}):
${textResponse}`
        );
      } else {
        throw new HttpRequestError(
          `${response.statusText} (${response.status}):
${textResponse}`
        );
      }
    }
  }
  if (opts.skipParseResponse) {
    return void 0;
  }
  try {
    const buffer = new Uint8Array(await response.arrayBuffer());
    return deserializeWithEncoding(
      opts.encoding,
      buffer,
      opts.responseVersionedDataHandler,
      opts.responseZodSchema,
      opts.responseFromJson,
      opts.responseFromBare
    );
  } catch (error) {
    throw new HttpRequestError(`Failed to parse response: ${error}`, {
      cause: error
    });
  }
}

// src/remote-manager-driver/log.ts
function logger3() {
  return getLogger("remote-manager-driver");
}

// src/remote-manager-driver/api-utils.ts
var EngineApiError = class extends Error {
  constructor(group, code, message) {
    super(message || `Engine API error: ${group}/${code}`);
    this.group = group;
    this.code = code;
    this.name = "EngineApiError";
  }
};
function getEndpoint(config4) {
  return config4.endpoint ?? "http://127.0.0.1:6420";
}
async function apiCall(config4, method, path, body) {
  const endpoint = getEndpoint(config4);
  const url = combineUrlPath(endpoint, path, {
    namespace: config4.namespace
  });
  logger3().debug({ msg: "making api call", method, url });
  const headers = {
    ...config4.headers
  };
  if (config4.token) {
    headers.Authorization = `Bearer ${config4.token}`;
  }
  return await sendHttpRequest({
    method,
    url,
    headers,
    body,
    encoding: "json",
    skipParseResponse: false,
    requestVersionedDataHandler: void 0,
    requestVersion: void 0,
    responseVersionedDataHandler: void 0,
    responseVersion: void 0,
    requestZodSchema: z4.any(),
    responseZodSchema: z4.any(),
    // Identity conversions (passthrough for generic API calls)
    requestToJson: (value) => value,
    requestToBare: (value) => value,
    responseFromJson: (value) => value,
    responseFromBare: (value) => value
  });
}

// src/remote-manager-driver/actor-websocket-client.ts
function buildActorGatewayUrl(endpoint, actorId, token, path = "") {
  const tokenSegment = token !== void 0 ? `@${encodeURIComponent(token)}` : "";
  const gatewayPath = `/gateway/${encodeURIComponent(actorId)}${tokenSegment}${path}`;
  return combineUrlPath(endpoint, gatewayPath);
}
async function openWebSocketToActor(runConfig, path, actorId, encoding, params) {
  const WebSocket2 = await importWebSocket();
  const endpoint = getEndpoint(runConfig);
  const guardUrl = buildActorGatewayUrl(
    endpoint,
    actorId,
    runConfig.token,
    path
  );
  logger3().debug({
    msg: "opening websocket to actor via guard",
    actorId,
    path,
    guardUrl
  });
  const ws = new WebSocket2(
    guardUrl,
    buildWebSocketProtocols(runConfig, encoding, params)
  );
  ws.binaryType = "arraybuffer";
  logger3().debug({ msg: "websocket connection opened", actorId });
  return ws;
}
function buildWebSocketProtocols(runConfig, encoding, params) {
  const protocols = [];
  protocols.push(WS_PROTOCOL_STANDARD);
  protocols.push(`${WS_PROTOCOL_ENCODING}${encoding}`);
  if (params) {
    protocols.push(
      `${WS_PROTOCOL_CONN_PARAMS}${encodeURIComponent(JSON.stringify(params))}`
    );
  }
  return protocols;
}

// src/remote-manager-driver/actor-http-client.ts
async function sendHttpRequestToActor(runConfig, actorId, actorRequest) {
  const url = new URL(actorRequest.url);
  const endpoint = getEndpoint(runConfig);
  const guardUrl = buildActorGatewayUrl(
    endpoint,
    actorId,
    runConfig.token,
    `${url.pathname}${url.search}`
  );
  let bodyToSend = null;
  const guardHeaders = buildGuardHeadersForHttp(
    runConfig,
    actorRequest,
    actorId
  );
  if (actorRequest.method !== "GET" && actorRequest.method !== "HEAD") {
    if (actorRequest.bodyUsed) {
      throw new Error("Request body has already been consumed");
    }
    const reqBody = await actorRequest.arrayBuffer();
    if (reqBody.byteLength !== 0) {
      bodyToSend = reqBody;
      guardHeaders.delete("transfer-encoding");
      guardHeaders.set("content-length", String(bodyToSend.byteLength));
    }
  }
  const guardRequest = new Request(guardUrl, {
    method: actorRequest.method,
    headers: guardHeaders,
    body: bodyToSend,
    signal: actorRequest.signal
  });
  return mutableResponse(await fetch(guardRequest));
}
function mutableResponse(fetchRes) {
  return new Response(fetchRes.body, fetchRes);
}
function buildGuardHeadersForHttp(runConfig, actorRequest, actorId) {
  const headers = new Headers();
  actorRequest.headers.forEach((value, key) => {
    headers.set(key, value);
  });
  for (const [key, value] of Object.entries(runConfig.headers)) {
    headers.set(key, value);
  }
  if (runConfig.token) {
    headers.set(HEADER_RIVET_TOKEN, runConfig.token);
  }
  return headers;
}

// src/remote-manager-driver/api-endpoints.ts
async function getActor(config4, _, actorId) {
  return apiCall(
    config4,
    "GET",
    `/actors?actor_ids=${encodeURIComponent(actorId)}`
  );
}
async function getActorByKey(config4, name, key) {
  const serializedKey = serializeActorKey(key);
  return apiCall(
    config4,
    "GET",
    `/actors?name=${encodeURIComponent(name)}&key=${encodeURIComponent(serializedKey)}`
  );
}
async function listActorsByName(config4, name) {
  return apiCall(
    config4,
    "GET",
    `/actors?name=${encodeURIComponent(name)}`
  );
}
async function getOrCreateActor(config4, request) {
  return apiCall(
    config4,
    "PUT",
    `/actors`,
    request
  );
}
async function createActor(config4, request) {
  return apiCall(
    config4,
    "POST",
    `/actors`,
    request
  );
}
async function destroyActor(config4, actorId) {
  return apiCall(
    config4,
    "DELETE",
    `/actors/${encodeURIComponent(actorId)}`
  );
}
async function getMetadata(config4) {
  return apiCall(config4, "GET", `/metadata`);
}
async function kvGet(config4, actorId, key) {
  return apiCall(
    config4,
    "GET",
    `/actors/${encodeURIComponent(actorId)}/kv/keys/${encodeURIComponent(key)}`
  );
}

// src/remote-manager-driver/metadata.ts
import pRetry from "p-retry";
var metadataLookupCache = /* @__PURE__ */ new Map();
async function lookupMetadataCached(config4) {
  const endpoint = getEndpoint(config4);
  const existingPromise = metadataLookupCache.get(endpoint);
  if (existingPromise) {
    return existingPromise;
  }
  const metadataLookupPromise = pRetry(
    async () => {
      logger3().debug({
        msg: "fetching metadata",
        endpoint
      });
      const metadataData = await getMetadata(config4);
      logger3().debug({
        msg: "received metadata",
        endpoint,
        clientEndpoint: metadataData.clientEndpoint
      });
      return metadataData;
    },
    {
      forever: true,
      minTimeout: 500,
      maxTimeout: 15e3,
      onFailedAttempt: (error) => {
        if (error.attemptNumber > 1) {
          logger3().warn({
            msg: "failed to fetch metadata, retrying",
            endpoint,
            attempt: error.attemptNumber,
            error: stringifyError(error)
          });
        }
      }
    }
  );
  metadataLookupCache.set(endpoint, metadataLookupPromise);
  return metadataLookupPromise;
}

// src/remote-manager-driver/ws-proxy.ts
async function createWebSocketProxy(c, targetUrl, protocols) {
  const WebSocket2 = await importWebSocket();
  const state = {};
  return {
    onOpen: async (event, clientWs) => {
      logger3().debug({ msg: "client websocket connected", targetUrl });
      if (clientWs.readyState !== 1) {
        logger3().warn({
          msg: "client websocket not open on connection",
          targetUrl,
          readyState: clientWs.readyState
        });
        return;
      }
      const targetWs = new WebSocket2(targetUrl, protocols);
      state.targetWs = targetWs;
      state.connectPromise = new Promise((resolve, reject) => {
        targetWs.addEventListener("open", () => {
          logger3().debug({
            msg: "target websocket connected",
            targetUrl
          });
          if (clientWs.readyState !== 1) {
            logger3().warn({
              msg: "client websocket closed before target connected",
              targetUrl,
              clientReadyState: clientWs.readyState
            });
            targetWs.close(1001, "Client disconnected");
            reject(new Error("Client disconnected"));
            return;
          }
          resolve();
        });
        targetWs.addEventListener("error", (error) => {
          logger3().warn({
            msg: "target websocket error during connection",
            targetUrl
          });
          reject(error);
        });
      });
      state.targetWs.addEventListener("message", (event2) => {
        if (typeof event2.data === "string" || event2.data instanceof ArrayBuffer) {
          clientWs.send(event2.data);
        } else if (event2.data instanceof Blob) {
          event2.data.arrayBuffer().then((buffer) => {
            clientWs.send(buffer);
          });
        }
      });
      state.targetWs.addEventListener("close", (event2) => {
        logger3().debug({
          msg: "target websocket closed",
          targetUrl,
          code: event2.code,
          reason: event2.reason
        });
        closeWebSocketIfOpen(clientWs, event2.code, event2.reason);
      });
      state.targetWs.addEventListener("error", (error) => {
        logger3().error({
          msg: "target websocket error",
          targetUrl,
          error: stringifyError(error)
        });
        closeWebSocketIfOpen(clientWs, 1011, "Target WebSocket error");
      });
    },
    onMessage: async (event, clientWs) => {
      if (!state.targetWs || !state.connectPromise) {
        logger3().error({
          msg: "websocket state not initialized",
          targetUrl
        });
        return;
      }
      try {
        await state.connectPromise;
        if (state.targetWs.readyState === WebSocket2.OPEN) {
          state.targetWs.send(event.data);
        } else {
          logger3().warn({
            msg: "target websocket not open",
            targetUrl,
            readyState: state.targetWs.readyState
          });
        }
      } catch (error) {
        logger3().error({
          msg: "failed to connect to target websocket",
          targetUrl,
          error
        });
        closeWebSocketIfOpen(
          clientWs,
          1011,
          "Failed to connect to target"
        );
      }
    },
    onClose: (event, clientWs) => {
      logger3().debug({
        msg: "client websocket closed",
        targetUrl,
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      if (state.targetWs) {
        if (state.targetWs.readyState === WebSocket2.OPEN || state.targetWs.readyState === WebSocket2.CONNECTING) {
          state.targetWs.close(
            1e3,
            event.reason || "Client disconnected"
          );
        }
      }
    },
    onError: (event, clientWs) => {
      logger3().error({ msg: "client websocket error", targetUrl, event });
      if (state.targetWs) {
        if (state.targetWs.readyState === WebSocket2.OPEN) {
          state.targetWs.close(1011, "Client WebSocket error");
        } else if (state.targetWs.readyState === WebSocket2.CONNECTING) {
          state.targetWs.close();
        }
      }
    }
  };
}
function closeWebSocketIfOpen(ws, code, reason) {
  if (ws.readyState === 1) {
    ws.close(code, reason);
  } else if ("close" in ws && ws.readyState === WebSocket.OPEN) {
    ws.close(code, reason);
  }
}

// src/remote-manager-driver/mod.ts
var RemoteManagerDriver = class {
  #config;
  #metadataPromise;
  constructor(runConfig) {
    if (getNextPhase() === "phase-production-build") {
      logger3().info(
        "detected next.js build phase, disabling health check"
      );
      runConfig.disableMetadataLookup = true;
    }
    this.#config = { ...runConfig };
    if (!runConfig.disableMetadataLookup) {
      this.#metadataPromise = lookupMetadataCached(this.#config).then(
        (metadataData) => {
          if (metadataData.clientEndpoint) {
            this.#config.endpoint = metadataData.clientEndpoint;
            if (metadataData.clientNamespace) {
              this.#config.namespace = metadataData.clientNamespace;
            }
            if (metadataData.clientToken) {
              this.#config.token = metadataData.clientToken;
            }
            logger3().info({
              msg: "overriding client endpoint",
              endpoint: metadataData.clientEndpoint,
              namespace: metadataData.clientNamespace,
              token: metadataData.clientToken
            });
          }
          logger3().info({
            msg: "connected to rivetkit manager",
            runtime: metadataData.runtime,
            version: metadataData.version,
            runner: metadataData.runner
          });
        }
      );
    }
  }
  async getForId({
    c,
    name,
    actorId
  }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const response = await getActor(this.#config, name, actorId);
    const actor = response.actors[0];
    if (!actor) return void 0;
    if (actor.name !== name) {
      logger3().debug({
        msg: "actor name mismatch from api",
        actorId,
        apiName: actor.name,
        requestedName: name
      });
      return void 0;
    }
    return apiActorToOutput(actor);
  }
  async getWithKey({
    c,
    name,
    key
  }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().debug({ msg: "getWithKey: searching for actor", name, key });
    try {
      const response = await getActorByKey(this.#config, name, key);
      const actor = response.actors[0];
      if (!actor) return void 0;
      logger3().debug({
        msg: "getWithKey: found actor via api",
        actorId: actor.actor_id,
        name,
        key
      });
      return apiActorToOutput(actor);
    } catch (error) {
      if (error instanceof EngineApiError && error.group === "actor" && error.code === "not_found") {
        return void 0;
      }
      throw error;
    }
  }
  async getOrCreateWithKey(input) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const { c, name, key, input: actorInput, region } = input;
    logger3().info({
      msg: "getOrCreateWithKey: getting or creating actor via engine api",
      name,
      key
    });
    const { actor, created } = await getOrCreateActor(this.#config, {
      datacenter: region,
      name,
      key: serializeActorKey(key),
      runner_name_selector: this.#config.runnerName,
      input: actorInput ? uint8ArrayToBase64(cbor3.encode(actorInput)) : void 0,
      crash_policy: "sleep"
    });
    logger3().info({
      msg: "getOrCreateWithKey: actor ready",
      actorId: actor.actor_id,
      name,
      key,
      created
    });
    return apiActorToOutput(actor);
  }
  async createActor({
    c,
    name,
    key,
    input,
    region
  }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().info({ msg: "creating actor via engine api", name, key });
    const result = await createActor(this.#config, {
      datacenter: region,
      name,
      runner_name_selector: this.#config.runnerName,
      key: serializeActorKey(key),
      input: input ? uint8ArrayToBase64(cbor3.encode(input)) : void 0,
      crash_policy: "sleep"
    });
    logger3().info({
      msg: "actor created",
      actorId: result.actor.actor_id,
      name,
      key
    });
    return apiActorToOutput(result.actor);
  }
  async listActors({ c, name }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().debug({ msg: "listing actors via engine api", name });
    const response = await listActorsByName(this.#config, name);
    return response.actors.map(apiActorToOutput);
  }
  async destroyActor(actorId) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().info({ msg: "destroying actor via engine api", actorId });
    await destroyActor(this.#config, actorId);
    logger3().info({ msg: "actor destroyed", actorId });
  }
  async sendRequest(actorId, actorRequest) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    return await sendHttpRequestToActor(
      this.#config,
      actorId,
      actorRequest
    );
  }
  async openWebSocket(path, actorId, encoding, params) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    return await openWebSocketToActor(
      this.#config,
      path,
      actorId,
      encoding,
      params
    );
  }
  async buildGatewayUrl(actorId) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const endpoint = getEndpoint(this.#config);
    return buildActorGatewayUrl(endpoint, actorId, this.#config.token);
  }
  async proxyRequest(_c, actorRequest, actorId) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    return await sendHttpRequestToActor(
      this.#config,
      actorId,
      actorRequest
    );
  }
  async proxyWebSocket(c, path, actorId, encoding, params) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const upgradeWebSocket = this.#config.getUpgradeWebSocket?.();
    invariant3(upgradeWebSocket, "missing getUpgradeWebSocket");
    const endpoint = getEndpoint(this.#config);
    const guardUrl = combineUrlPath(endpoint, path);
    const wsGuardUrl = guardUrl.replace("http://", "ws://");
    logger3().debug({
      msg: "forwarding websocket to actor via guard",
      actorId,
      path,
      guardUrl
    });
    const protocols = buildWebSocketProtocols(
      this.#config,
      encoding,
      params
    );
    const args = await createWebSocketProxy(c, wsGuardUrl, protocols);
    return await upgradeWebSocket(() => args)(c, noopNext());
  }
  async kvGet(actorId, key) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().debug({ msg: "getting kv value via engine api", key });
    const response = await kvGet(
      this.#config,
      actorId,
      new TextDecoder("utf8").decode(key)
    );
    return response.value;
  }
  displayInformation() {
    return { properties: {} };
  }
  setGetUpgradeWebSocket(getUpgradeWebSocket) {
    this.#config.getUpgradeWebSocket = getUpgradeWebSocket;
  }
};
function apiActorToOutput(actor) {
  return {
    actorId: actor.actor_id,
    name: actor.name,
    key: deserializeActorKey(actor.key),
    createTs: actor.create_ts,
    startTs: actor.start_ts ?? null,
    connectableTs: actor.connectable_ts ?? null,
    sleepTs: actor.sleep_ts ?? null,
    destroyTs: actor.destroy_ts ?? null,
    error: actor.error ?? void 0
  };
}

// src/client/actor-conn.ts
import * as cbor6 from "cbor-x";
import invariant4 from "invariant";
import pRetry2 from "p-retry";

// src/actor/protocol/old.ts
import * as cbor4 from "cbor-x";

// src/actor/instance/keys.ts
var KEYS = {
  PERSIST_DATA: Uint8Array.from([1]),
  CONN_PREFIX: Uint8Array.from([2]),
  // Prefix for connection keys
  INSPECTOR_TOKEN: Uint8Array.from([3]),
  // Inspector token key
  KV: Uint8Array.from([4]),
  // Prefix for user-facing KV storage
  QUEUE_PREFIX: Uint8Array.from([5]),
  // Prefix for queue storage
  WORKFLOW_PREFIX: Uint8Array.from([6]),
  // Prefix for workflow storage
  TRACES_PREFIX: Uint8Array.from([7]),
  // Prefix for traces storage
  SQLITE_PREFIX: Uint8Array.from([8])
  // Prefix for SQLite VFS data
};
var STORAGE_VERSION = {
  QUEUE: 1,
  WORKFLOW: 1,
  TRACES: 1,
  SQLITE: 1
};
var STORAGE_VERSION_BYTES = {
  QUEUE: Uint8Array.from([STORAGE_VERSION.QUEUE]),
  WORKFLOW: Uint8Array.from([STORAGE_VERSION.WORKFLOW]),
  TRACES: Uint8Array.from([STORAGE_VERSION.TRACES]),
  SQLITE: Uint8Array.from([STORAGE_VERSION.SQLITE])
};
var QUEUE_NAMESPACE = {
  METADATA: Uint8Array.from([1]),
  MESSAGES: Uint8Array.from([2])
};
function concatPrefix(prefix, suffix) {
  const merged = new Uint8Array(prefix.length + suffix.length);
  merged.set(prefix, 0);
  merged.set(suffix, prefix.length);
  return merged;
}
var QUEUE_STORAGE_PREFIX = concatPrefix(
  KEYS.QUEUE_PREFIX,
  STORAGE_VERSION_BYTES.QUEUE
);
var QUEUE_METADATA_KEY = concatPrefix(
  QUEUE_STORAGE_PREFIX,
  QUEUE_NAMESPACE.METADATA
);
var QUEUE_MESSAGES_PREFIX = concatPrefix(
  QUEUE_STORAGE_PREFIX,
  QUEUE_NAMESPACE.MESSAGES
);
var WORKFLOW_STORAGE_PREFIX = concatPrefix(
  KEYS.WORKFLOW_PREFIX,
  STORAGE_VERSION_BYTES.WORKFLOW
);
var TRACES_STORAGE_PREFIX = concatPrefix(
  KEYS.TRACES_PREFIX,
  STORAGE_VERSION_BYTES.TRACES
);
var SQLITE_STORAGE_PREFIX = concatPrefix(
  KEYS.SQLITE_PREFIX,
  STORAGE_VERSION_BYTES.SQLITE
);

// src/actor/protocol/old.ts
async function inputDataToBuffer(data) {
  if (typeof data === "string") {
    return data;
  } else if (data instanceof Blob) {
    const arrayBuffer = await data.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } else if (data instanceof Uint8Array) {
    return data;
  } else if (data instanceof ArrayBuffer || data instanceof SharedArrayBuffer) {
    return new Uint8Array(data);
  } else {
    throw new MalformedMessage();
  }
}

// src/client/actor-query.ts
async function queryActor(c, query, managerDriver) {
  logger2().debug({ msg: "querying actor", query: JSON.stringify(query) });
  let actorOutput;
  if ("getForId" in query) {
    const output = await managerDriver.getForId({
      c,
      name: query.getForId.name,
      actorId: query.getForId.actorId
    });
    if (!output) throw new ActorNotFound(query.getForId.actorId);
    actorOutput = output;
  } else if ("getForKey" in query) {
    const existingActor = await managerDriver.getWithKey({
      c,
      name: query.getForKey.name,
      key: query.getForKey.key
    });
    if (!existingActor) {
      throw new ActorNotFound(
        `${query.getForKey.name}:${JSON.stringify(query.getForKey.key)}`
      );
    }
    actorOutput = existingActor;
  } else if ("getOrCreateForKey" in query) {
    const getOrCreateOutput = await managerDriver.getOrCreateWithKey({
      c,
      name: query.getOrCreateForKey.name,
      key: query.getOrCreateForKey.key,
      input: query.getOrCreateForKey.input,
      region: query.getOrCreateForKey.region
    });
    actorOutput = {
      actorId: getOrCreateOutput.actorId
    };
  } else if ("create" in query) {
    const createOutput = await managerDriver.createActor({
      c,
      name: query.create.name,
      key: query.create.key,
      input: query.create.input,
      region: query.create.region
    });
    actorOutput = {
      actorId: createOutput.actorId
    };
  } else {
    throw new InvalidRequest("Invalid query format");
  }
  logger2().debug({ msg: "actor query result", actorId: actorOutput.actorId });
  return { actorId: actorOutput.actorId };
}
function getActorNameFromQuery(query) {
  if ("getForId" in query) return query.getForId.name;
  if ("getForKey" in query) return query.getForKey.name;
  if ("getOrCreateForKey" in query) return query.getOrCreateForKey.name;
  if ("create" in query) return query.create.name;
  throw new InvalidRequest("Invalid query format");
}
async function checkForSchedulingError(group, code, actorId, query, driver) {
  const name = getActorNameFromQuery(query);
  try {
    const actor = await driver.getForId({ name, actorId });
    if (actor?.error) {
      logger2().info({
        msg: "found actor scheduling error",
        actorId,
        error: actor.error
      });
      return new ActorSchedulingError(group, code, actorId, actor.error);
    }
  } catch (err) {
    logger2().warn({
      msg: "failed to fetch actor details for scheduling error check",
      actorId,
      error: stringifyError(err)
    });
  }
  return null;
}

// src/client/queue.ts
import * as cbor5 from "cbor-x";

// src/utils/endpoint-parser.ts
function tryParseEndpoint(ctx, options) {
  const {
    endpoint,
    path = ["endpoint"],
    namespace: configNamespace,
    token: configToken
  } = options;
  let url;
  try {
    url = new URL(endpoint);
  } catch {
    ctx.addIssue({
      code: "custom",
      message: `invalid URL: ${endpoint}`,
      path
    });
    return void 0;
  }
  if (url.search) {
    ctx.addIssue({
      code: "custom",
      message: "endpoint cannot contain a query string",
      path
    });
    return void 0;
  }
  if (url.hash) {
    ctx.addIssue({
      code: "custom",
      message: "endpoint cannot contain a fragment",
      path
    });
    return void 0;
  }
  const namespace = url.username ? decodeURIComponent(url.username) : void 0;
  const token = url.password ? decodeURIComponent(url.password) : void 0;
  if (token && !namespace) {
    ctx.addIssue({
      code: "custom",
      message: "endpoint cannot have a token without a namespace",
      path
    });
    return void 0;
  }
  if (namespace && configNamespace) {
    ctx.addIssue({
      code: "custom",
      message: "cannot specify namespace both in endpoint URL and as a separate config option",
      path: ["namespace"]
    });
  }
  if (token && configToken) {
    ctx.addIssue({
      code: "custom",
      message: "cannot specify token both in endpoint URL and as a separate config option",
      path: ["token"]
    });
  }
  url.username = "";
  url.password = "";
  const cleanedEndpoint = url.toString();
  return {
    endpoint: cleanedEndpoint,
    namespace,
    token
  };
}

// src/client/queue.ts
function createQueueSender(senderOptions) {
  async function send(name, body, options) {
    const wait = options?.wait ?? false;
    const timeout = options?.timeout;
    const result = await sendHttpRequest({
      url: `http://actor/queue/${encodeURIComponent(name)}`,
      method: "POST",
      headers: {
        [HEADER_ENCODING]: senderOptions.encoding,
        ...senderOptions.params !== void 0 ? {
          [HEADER_CONN_PARAMS]: JSON.stringify(
            senderOptions.params
          )
        } : {}
      },
      body: { body, wait, timeout },
      encoding: senderOptions.encoding,
      customFetch: senderOptions.customFetch,
      signal: options?.signal,
      requestVersion: CURRENT_VERSION,
      requestVersionedDataHandler: HTTP_QUEUE_SEND_REQUEST_VERSIONED,
      responseVersion: CURRENT_VERSION,
      responseVersionedDataHandler: HTTP_QUEUE_SEND_RESPONSE_VERSIONED,
      requestZodSchema: HttpQueueSendRequestSchema,
      responseZodSchema: HttpQueueSendResponseSchema,
      requestToJson: (value) => ({
        ...value,
        name
      }),
      requestToBare: (value) => ({
        name: value.name ?? name,
        body: bufferToArrayBuffer(cbor5.encode(value.body)),
        wait: value.wait ?? false,
        timeout: value.timeout !== void 0 ? BigInt(value.timeout) : null
      }),
      responseFromJson: (json) => {
        if (json.response === void 0) {
          return { status: json.status };
        }
        return {
          status: json.status,
          response: json.response
        };
      },
      responseFromBare: (bare4) => {
        if (bare4.response === null || bare4.response === void 0) {
          return { status: bare4.status };
        }
        return {
          status: bare4.status,
          response: cbor5.decode(new Uint8Array(bare4.response))
        };
      }
    });
    if (wait) {
      return result;
    }
    return;
  }
  return {
    send
  };
}

// src/client/actor-conn.ts
var CONNECT_SYMBOL = /* @__PURE__ */ Symbol("connect");
var ActorConnRaw = class {
  #disposed = false;
  /* Will be aborted on dispose. */
  #abortController = new AbortController();
  #connStatus = "idle";
  #actorId;
  #connId;
  #messageQueue = [];
  #actionsInFlight = /* @__PURE__ */ new Map();
  // biome-ignore lint/suspicious/noExplicitAny: Unknown subscription type
  #eventSubscriptions = /* @__PURE__ */ new Map();
  #errorHandlers = /* @__PURE__ */ new Set();
  #openHandlers = /* @__PURE__ */ new Set();
  #openScheduled = false;
  #closeHandlers = /* @__PURE__ */ new Set();
  #statusChangeHandlers = /* @__PURE__ */ new Set();
  #actionIdCounter = 0;
  #queueSender;
  /**
   * Interval that keeps the NodeJS process alive if this is the only thing running.
   *
   * See ttps://github.com/nodejs/node/issues/22088
   */
  #keepNodeAliveInterval;
  /** Promise used to indicate the socket has connected successfully. This will be rejected if the connection fails. */
  #onOpenPromise;
  #websocket;
  #client;
  #driver;
  #params;
  #getParams;
  #encoding;
  #actorQuery;
  // TODO: ws message queue
  /**
   * Do not call this directly.
   *
   * Creates an instance of ActorConnRaw.
   *
   * @protected
   */
  constructor(client, driver, params, getParams, encoding, actorQuery) {
    this.#client = client;
    this.#driver = driver;
    this.#params = params;
    this.#getParams = getParams;
    this.#encoding = encoding;
    this.#actorQuery = actorQuery;
    this.#queueSender = createQueueSender({
      encoding: this.#encoding,
      params: this.#params,
      customFetch: async (request) => {
        if (!this.#actorId) {
          const { actorId } = await queryActor(
            void 0,
            this.#actorQuery,
            this.#driver
          );
          this.#actorId = actorId;
        }
        return this.#driver.sendRequest(this.#actorId, request);
      }
    });
    this.#keepNodeAliveInterval = setInterval(() => 6e4);
  }
  send(name, body, options) {
    return this.#queueSender.send(name, body, options);
  }
  /**
   * Call a raw action connection. See {@link ActorConn} for type-safe action calls.
   *
   * @see {@link ActorConn}
   * @template Args - The type of arguments to pass to the action function.
   * @template Response - The type of the response returned by the action function.
   * @param {string} name - The name of the action function to call.
   * @param {...Args} args - The arguments to pass to the action function.
   * @returns {Promise<Response>} - A promise that resolves to the response of the action function.
   */
  async action(opts) {
    if (typeof opts === "string" || typeof opts !== "object" || opts === null || !("name" in opts)) {
      throw new Error(
        `Invalid action call: expected an options object { name, args }, got ${typeof opts}. Use conn.actionName(...args) for the shorthand API.`
      );
    }
    logger2().debug({ msg: "action", name: opts.name, args: opts.args });
    const actionId = this.#actionIdCounter;
    this.#actionIdCounter += 1;
    const { promise, resolve, reject } = promiseWithResolvers(
      (reason) => logger2().warn({
        msg: "unhandled action promise rejection",
        reason
      })
    );
    this.#actionsInFlight.set(actionId, {
      name: opts.name,
      resolve,
      reject
    });
    logger2().debug({
      msg: "added action to in-flight map",
      actionId,
      actionName: opts.name,
      inFlightCount: this.#actionsInFlight.size
    });
    this.#sendMessage({
      body: {
        tag: "ActionRequest",
        val: {
          id: BigInt(actionId),
          name: opts.name,
          args: opts.args
        }
      }
    });
    const { id: responseId, output } = await promise;
    if (responseId !== BigInt(actionId))
      throw new Error(
        `Request ID ${actionId} does not match response ID ${responseId}`
      );
    return output;
  }
  /**
   * Do not call this directly.
   * Establishes a connection to the server using the specified endpoint & encoding & driver.
   *
   * @protected
   */
  [CONNECT_SYMBOL]() {
    this.#connectWithRetry();
  }
  #setConnStatus(status) {
    const prevStatus = this.#connStatus;
    if (prevStatus === status) return;
    this.#connStatus = status;
    for (const handler of [...this.#statusChangeHandlers]) {
      try {
        handler(status);
      } catch (err) {
        logger2().error({
          msg: "error in status change handler",
          error: stringifyError(err)
        });
      }
    }
    if (status === "connected") {
      for (const handler of [...this.#openHandlers]) {
        try {
          handler();
        } catch (err) {
          logger2().error({
            msg: "error in open handler",
            error: stringifyError(err)
          });
        }
      }
    }
    if ((status === "disconnected" || status === "idle") && prevStatus === "connected") {
      for (const handler of [...this.#closeHandlers]) {
        try {
          handler();
        } catch (err) {
          logger2().error({
            msg: "error in close handler",
            error: stringifyError(err)
          });
        }
      }
    }
  }
  #connectWithRetry() {
    this.#setConnStatus("connecting");
    pRetry2(this.#connectAndWait.bind(this), {
      forever: true,
      minTimeout: 250,
      maxTimeout: 3e4,
      onFailedAttempt: (error) => {
        logger2().warn({
          msg: "failed to reconnect",
          attempt: error.attemptNumber,
          error: stringifyError(error)
        });
      },
      // Cancel retry if aborted
      signal: this.#abortController.signal
    }).catch((err) => {
      if (err.name === "AbortError") {
        logger2().info({ msg: "connection retry aborted" });
      } else {
        logger2().error({
          msg: "unexpected error in connection retry",
          error: stringifyError(err)
        });
      }
    });
  }
  async #connectAndWait() {
    try {
      if (this.#onOpenPromise)
        throw new Error("#onOpenPromise already defined");
      this.#onOpenPromise = promiseWithResolvers(
        (reason) => logger2().warn({
          msg: "unhandled open promise rejection",
          reason
        })
      );
      await this.#connectWebSocket();
      await this.#onOpenPromise.promise;
    } finally {
      this.#onOpenPromise = void 0;
    }
  }
  #clearQueuedMessages() {
    if (this.#messageQueue.length === 0) return;
    logger2().debug({
      msg: "clearing queued connection messages",
      queueLength: this.#messageQueue.length
    });
    this.#messageQueue = [];
  }
  async #resolveConnectionParams() {
    if (!this.#getParams) {
      return this.#params;
    }
    try {
      return await this.#getParams();
    } catch (err) {
      const errorMessage = stringifyError(err);
      const error = new ActorError2(
        "client",
        "get_params_failed",
        `Failed to resolve connection params: ${errorMessage}`,
        { error: errorMessage }
      );
      this.#clearQueuedMessages();
      this.#rejectPendingPromises(error, false);
      this.#dispatchActorError(error);
      throw error;
    }
  }
  async #connectWebSocket() {
    const { actorId } = await queryActor(
      void 0,
      this.#actorQuery,
      this.#driver
    );
    this.#actorId = actorId;
    const params = await this.#resolveConnectionParams();
    const ws = await this.#driver.openWebSocket(
      PATH_CONNECT,
      actorId,
      this.#encoding,
      params
    );
    logger2().debug({
      msg: "opened websocket",
      connId: this.#connId,
      readyState: ws.readyState,
      messageQueueLength: this.#messageQueue.length
    });
    this.#websocket = ws;
    ws.addEventListener("open", () => {
      logger2().debug({
        msg: "client websocket open",
        connId: this.#connId
      });
    });
    ws.addEventListener("message", async (ev) => {
      try {
        await this.#handleOnMessage(ev.data);
      } catch (err) {
        logger2().error({
          msg: "error in websocket message handler",
          error: stringifyError(err)
        });
      }
    });
    ws.addEventListener("close", async (ev) => {
      try {
        await this.#handleOnClose(ev);
      } catch (err) {
        logger2().error({
          msg: "error in websocket close handler",
          error: stringifyError(err)
        });
      }
    });
    ws.addEventListener("error", () => {
      try {
        this.#handleOnError();
      } catch (err) {
        logger2().error({
          msg: "error in websocket error handler",
          error: stringifyError(err)
        });
      }
    });
  }
  /** Called by the onopen event from drivers. */
  #handleOnOpen() {
    if (this.#disposed) {
      logger2().debug({
        msg: "handleOnOpen called after dispose, closing websocket"
      });
      if (this.#websocket) {
        this.#websocket.close(1e3, "Disposed");
        this.#websocket = void 0;
      }
      return;
    }
    if (this.#connStatus === "connected" || this.#openScheduled) {
      return;
    }
    this.#openScheduled = true;
    queueMicrotask(() => {
      this.#openScheduled = false;
      if (this.#disposed) {
        logger2().debug({
          msg: "handleOnOpen scheduled after dispose, closing websocket"
        });
        if (this.#websocket) {
          this.#websocket.close(1e3, "Disposed");
          this.#websocket = void 0;
        }
        return;
      }
      logger2().debug({
        msg: "socket open",
        messageQueueLength: this.#messageQueue.length,
        connId: this.#connId
      });
      this.#setConnStatus("connected");
      if (this.#onOpenPromise) {
        this.#onOpenPromise.resolve(void 0);
      } else {
        logger2().warn({ msg: "#onOpenPromise is undefined" });
      }
      for (const eventName of this.#eventSubscriptions.keys()) {
        this.#sendSubscription(eventName, true);
      }
      const queue = this.#messageQueue;
      this.#messageQueue = [];
      logger2().debug({
        msg: "flushing message queue",
        queueLength: queue.length
      });
      for (const msg of queue) {
        this.#sendMessage(msg);
      }
    });
  }
  /** Called by the onmessage event from drivers. */
  async #handleOnMessage(data) {
    logger2().trace({
      msg: "received message",
      dataType: typeof data,
      isBlob: data instanceof Blob,
      isArrayBuffer: data instanceof ArrayBuffer
    });
    const response = await this.#parseMessage(data);
    logger2().trace(
      getLogMessage() ? {
        msg: "parsed message",
        message: jsonStringifyCompat(response).substring(0, 100) + "..."
      } : { msg: "parsed message" }
    );
    if (response.body.tag === "Init") {
      this.#actorId = response.body.val.actorId;
      this.#connId = response.body.val.connectionId;
      logger2().trace({
        msg: "received init message",
        actorId: this.#actorId,
        connId: this.#connId
      });
      this.#handleOnOpen();
    } else if (response.body.tag === "Error") {
      const { group, code, message, metadata, actionId } = response.body.val;
      if (actionId) {
        const inFlight = this.#takeActionInFlight(Number(actionId));
        logger2().warn({
          msg: "action error",
          actionId,
          actionName: inFlight?.name,
          group,
          code,
          message,
          metadata
        });
        inFlight.reject(
          new ActorError2(group, code, message, metadata)
        );
      } else {
        logger2().warn({
          msg: "connection error",
          group,
          code,
          message,
          metadata
        });
        let errorToThrow = new ActorError2(
          group,
          code,
          message,
          metadata
        );
        if (isSchedulingError(group, code) && this.#actorId) {
          const schedulingError = await checkForSchedulingError(
            group,
            code,
            this.#actorId,
            this.#actorQuery,
            this.#driver
          );
          if (schedulingError) {
            errorToThrow = schedulingError;
          }
        }
        if (this.#onOpenPromise) {
          this.#onOpenPromise.reject(errorToThrow);
        }
        for (const [id, inFlight] of this.#actionsInFlight.entries()) {
          inFlight.reject(errorToThrow);
          this.#actionsInFlight.delete(id);
        }
        this.#dispatchActorError(errorToThrow);
      }
    } else if (response.body.tag === "ActionResponse") {
      const { id: actionId } = response.body.val;
      logger2().debug({
        msg: "received action response",
        actionId: Number(actionId),
        inFlightCount: this.#actionsInFlight.size,
        inFlightIds: Array.from(this.#actionsInFlight.keys())
      });
      const inFlight = this.#takeActionInFlight(Number(actionId));
      logger2().trace({
        msg: "resolving action promise",
        actionId,
        actionName: inFlight?.name
      });
      inFlight.resolve(response.body.val);
    } else if (response.body.tag === "Event") {
      logger2().trace({
        msg: "received event",
        name: response.body.val.name
      });
      this.#dispatchEvent(response.body.val);
    } else {
      assertUnreachable(response.body);
    }
  }
  /** Called by the onclose event from drivers. */
  async #handleOnClose(event) {
    const closeEvent = event;
    const wasClean = closeEvent.wasClean;
    const wasConnected = this.#connStatus === "connected";
    logger2().info({
      msg: "socket closed",
      code: closeEvent.code,
      reason: closeEvent.reason,
      wasClean,
      disposed: this.#disposed,
      connId: this.#connId
    });
    this.#websocket = void 0;
    if (this.#disposed) {
      this.#rejectPendingPromises(new ActorConnDisposed(), true);
    } else {
      this.#setConnStatus("disconnected");
      let error;
      const reason = closeEvent.reason || "";
      const parsed = parseWebSocketCloseReason(reason);
      if (parsed) {
        const { group, code } = parsed;
        if (isSchedulingError(group, code) && this.#actorId) {
          const schedulingError = await checkForSchedulingError(
            group,
            code,
            this.#actorId,
            this.#actorQuery,
            this.#driver
          );
          if (schedulingError) {
            error = schedulingError;
          } else {
            error = new ActorError2(
              group,
              code,
              `Connection closed: ${reason}`,
              void 0
            );
          }
        } else {
          error = new ActorError2(
            group,
            code,
            `Connection closed: ${reason}`,
            void 0
          );
        }
      } else {
        error = new Error(
          `${wasClean ? "Connection closed" : "Connection lost"} (code: ${closeEvent.code}, reason: ${reason})`
        );
      }
      this.#rejectPendingPromises(error, false);
      if (error instanceof ActorError2) {
        this.#dispatchActorError(error);
      }
      if (wasConnected) {
        logger2().debug({
          msg: "triggering reconnect",
          connId: this.#connId
        });
        this.#connectWithRetry();
      }
    }
  }
  #rejectPendingPromises(error, suppressUnhandled) {
    if (this.#onOpenPromise) {
      if (suppressUnhandled) {
        this.#onOpenPromise.promise.catch(() => {
        });
      }
      this.#onOpenPromise.reject(error);
    }
    for (const actionInfo of this.#actionsInFlight.values()) {
      actionInfo.reject(error);
    }
    this.#actionsInFlight.clear();
  }
  /** Called by the onerror event from drivers. */
  #handleOnError() {
    if (this.#disposed) return;
    logger2().warn("socket error");
  }
  #takeActionInFlight(id) {
    const inFlight = this.#actionsInFlight.get(id);
    if (!inFlight) {
      logger2().error({
        msg: "action not found in in-flight map",
        lookupId: id,
        inFlightCount: this.#actionsInFlight.size,
        inFlightIds: Array.from(this.#actionsInFlight.keys()),
        inFlightActions: Array.from(
          this.#actionsInFlight.entries()
        ).map(([id2, action]) => ({
          id: id2,
          name: action.name
        }))
      });
      throw new InternalError2(`No in flight response for ${id}`);
    }
    this.#actionsInFlight.delete(id);
    logger2().debug({
      msg: "removed action from in-flight map",
      actionId: id,
      actionName: inFlight.name,
      inFlightCount: this.#actionsInFlight.size
    });
    return inFlight;
  }
  #dispatchEvent(event) {
    const { name, args } = event;
    const listeners = this.#eventSubscriptions.get(name);
    if (!listeners) return;
    for (const listener of [...listeners]) {
      listener.callback(...args);
      if (listener.once) {
        listeners.delete(listener);
      }
    }
    if (listeners.size === 0) {
      this.#eventSubscriptions.delete(name);
    }
  }
  #dispatchActorError(error) {
    for (const handler of [...this.#errorHandlers]) {
      try {
        handler(error);
      } catch (err) {
        logger2().error({
          msg: "error in connection error handler",
          error: stringifyError(err)
        });
      }
    }
  }
  #addEventSubscription(eventName, callback, once) {
    const listener = {
      callback,
      once
    };
    let subscriptionSet = this.#eventSubscriptions.get(eventName);
    if (subscriptionSet === void 0) {
      subscriptionSet = /* @__PURE__ */ new Set();
      this.#eventSubscriptions.set(eventName, subscriptionSet);
      this.#sendSubscription(eventName, true);
    }
    subscriptionSet.add(listener);
    return () => {
      const listeners = this.#eventSubscriptions.get(eventName);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.#eventSubscriptions.delete(eventName);
          this.#sendSubscription(eventName, false);
        }
      }
    };
  }
  /**
   * Subscribes to an event that will happen repeatedly.
   *
   * @template Args - The type of arguments the event callback will receive.
   * @param {string} eventName - The name of the event to subscribe to.
   * @param {(...args: Args) => void} callback - The callback function to execute when the event is triggered.
   * @returns {EventUnsubscribe} - A function to unsubscribe from the event.
   * @see {@link https://rivet.dev/docs/events|Events Documentation}
   */
  on(eventName, callback) {
    return this.#addEventSubscription(eventName, callback, false);
  }
  /**
   * Subscribes to an event that will be triggered only once.
   *
   * @template Args - The type of arguments the event callback will receive.
   * @param {string} eventName - The name of the event to subscribe to.
   * @param {(...args: Args) => void} callback - The callback function to execute when the event is triggered.
   * @returns {EventUnsubscribe} - A function to unsubscribe from the event.
   * @see {@link https://rivet.dev/docs/events|Events Documentation}
   */
  once(eventName, callback) {
    return this.#addEventSubscription(eventName, callback, true);
  }
  /**
   * Subscribes to connection errors.
   *
   * @param {ActorErrorCallback} callback - The callback function to execute when a connection error occurs.
   * @returns {() => void} - A function to unsubscribe from the error handler.
   */
  onError(callback) {
    this.#errorHandlers.add(callback);
    return () => {
      this.#errorHandlers.delete(callback);
    };
  }
  /**
   * Returns the current connection status.
   *
   * @returns {ActorConnStatus} - The current connection status.
   */
  get connStatus() {
    return this.#connStatus;
  }
  /**
   * Returns whether the connection is currently open.
   *
   * @deprecated Use `connStatus` instead.
   * @returns {boolean} - True if the connection is open, false otherwise.
   */
  get isConnected() {
    return this.#connStatus === "connected";
  }
  /**
   * Subscribes to connection open events.
   *
   * This is called when the WebSocket connection is established and the Init message is received.
   *
   * @param {ConnectionStateCallback} callback - The callback function to execute when the connection opens.
   * @returns {() => void} - A function to unsubscribe from the open handler.
   */
  onOpen(callback) {
    this.#openHandlers.add(callback);
    return () => {
      this.#openHandlers.delete(callback);
    };
  }
  /**
   * Subscribes to connection close events.
   *
   * This is called when the WebSocket connection is closed. The connection will automatically
   * attempt to reconnect unless disposed.
   *
   * @param {ConnectionStateCallback} callback - The callback function to execute when the connection closes.
   * @returns {() => void} - A function to unsubscribe from the close handler.
   */
  onClose(callback) {
    this.#closeHandlers.add(callback);
    return () => {
      this.#closeHandlers.delete(callback);
    };
  }
  /**
   * Subscribes to connection status changes.
   *
   * This is called whenever the connection status changes between Disconnected, Connecting, and Connected.
   *
   * @param {StatusChangeCallback} callback - The callback function to execute when the status changes.
   * @returns {() => void} - A function to unsubscribe from the status change handler.
   */
  onStatusChange(callback) {
    this.#statusChangeHandlers.add(callback);
    return () => {
      this.#statusChangeHandlers.delete(callback);
    };
  }
  #sendMessage(message, opts) {
    if (this.#disposed) {
      if (opts?.ephemeral) {
        return;
      } else {
        throw new ActorConnDisposed();
      }
    }
    let queueMessage = false;
    if (this.#websocket) {
      const readyState = this.#websocket.readyState;
      logger2().debug({
        msg: "websocket send attempt",
        readyState,
        readyStateString: readyState === 0 ? "CONNECTING" : readyState === 1 ? "OPEN" : readyState === 2 ? "CLOSING" : "CLOSED",
        connId: this.#connId,
        messageType: message.body.tag,
        actionName: message.body.val?.name
      });
      if (this.#connStatus !== "connected") {
        logger2().debug({
          msg: "websocket init pending, queueing message",
          connStatus: this.#connStatus,
          messageType: message.body.tag
        });
        queueMessage = true;
      } else if (readyState === 1) {
        try {
          const messageSerialized = serializeWithEncoding(
            this.#encoding,
            message,
            TO_SERVER_VERSIONED,
            CURRENT_VERSION,
            ToServerSchema,
            // JSON: args is the raw value
            (msg) => msg,
            // BARE: args needs to be CBOR-encoded to ArrayBuffer
            (msg) => {
              if (msg.body.tag === "ActionRequest") {
                return {
                  body: {
                    tag: "ActionRequest",
                    val: {
                      id: msg.body.val.id,
                      name: msg.body.val.name,
                      args: bufferToArrayBuffer(
                        cbor6.encode(msg.body.val.args)
                      )
                    }
                  }
                };
              } else {
                return msg;
              }
            }
          );
          this.#websocket.send(messageSerialized);
          logger2().trace({
            msg: "sent websocket message",
            len: messageLength(messageSerialized)
          });
        } catch (error) {
          logger2().warn({
            msg: "failed to send message, added to queue",
            error,
            connId: this.#connId
          });
          queueMessage = true;
        }
      } else {
        logger2().debug({
          msg: "websocket not open, queueing message",
          readyState
        });
        queueMessage = true;
      }
    } else {
      logger2().debug({ msg: "no websocket, queueing message" });
      queueMessage = true;
    }
    if (!opts?.ephemeral && queueMessage) {
      this.#messageQueue.push(message);
      logger2().debug({
        msg: "queued connection message",
        queueLength: this.#messageQueue.length,
        connId: this.#connId,
        messageType: message.body.tag,
        actionName: message.body.val?.name
      });
    }
  }
  async #parseMessage(data) {
    invariant4(this.#websocket, "websocket must be defined");
    const buffer = await inputDataToBuffer(data);
    return deserializeWithEncoding(
      this.#encoding,
      buffer,
      TO_CLIENT_VERSIONED,
      ToClientSchema,
      // JSON: values are already the correct type
      (msg) => msg,
      // BARE: need to decode ArrayBuffer fields back to unknown
      (msg) => {
        if (msg.body.tag === "Error") {
          return {
            body: {
              tag: "Error",
              val: {
                group: msg.body.val.group,
                code: msg.body.val.code,
                message: msg.body.val.message,
                metadata: msg.body.val.metadata ? cbor6.decode(
                  new Uint8Array(
                    msg.body.val.metadata
                  )
                ) : null,
                actionId: msg.body.val.actionId
              }
            }
          };
        } else if (msg.body.tag === "ActionResponse") {
          return {
            body: {
              tag: "ActionResponse",
              val: {
                id: msg.body.val.id,
                output: cbor6.decode(
                  new Uint8Array(msg.body.val.output)
                )
              }
            }
          };
        } else if (msg.body.tag === "Event") {
          return {
            body: {
              tag: "Event",
              val: {
                name: msg.body.val.name,
                args: cbor6.decode(
                  new Uint8Array(msg.body.val.args)
                )
              }
            }
          };
        } else {
          return msg;
        }
      }
    );
  }
  /**
   * Get the actor ID (for testing purposes).
   * @internal
   */
  get actorId() {
    return this.#actorId;
  }
  /**
   * Get the connection ID (for testing purposes).
   * @internal
   */
  get connId() {
    return this.#connId;
  }
  /**
   * Get the connection ID (for testing purposes).
   * @internal
   * @deprecated Use `connId` instead.
   */
  get connectionId() {
    return this.#connId;
  }
  /**
   * Disconnects from the actor.
   *
   * @returns {Promise<void>} A promise that resolves when the socket is gracefully closed.
   */
  async dispose() {
    if (this.#disposed) {
      logger2().warn({ msg: "connection already disconnected" });
      return;
    }
    this.#disposed = true;
    logger2().debug({ msg: "disposing actor conn" });
    this.#setConnStatus("idle");
    clearInterval(this.#keepNodeAliveInterval);
    this.#abortController.abort();
    this.#client[ACTOR_CONNS_SYMBOL].delete(this);
    if (this.#websocket) {
      const ws = this.#websocket;
      if (ws.readyState !== 2 && ws.readyState !== 3) {
        const { promise, resolve } = promiseWithResolvers(
          (reason) => logger2().warn({
            msg: "unhandled websocket close promise rejection",
            reason
          })
        );
        ws.addEventListener("close", () => resolve(void 0));
        ws.close(1e3, "Disposed");
        await promise;
      }
    } else {
      this.#rejectPendingPromises(new ActorConnDisposed(), true);
    }
    this.#websocket = void 0;
  }
  #sendSubscription(eventName, subscribe) {
    this.#sendMessage(
      {
        body: {
          tag: "SubscriptionRequest",
          val: {
            eventName,
            subscribe
          }
        }
      },
      { ephemeral: true }
    );
  }
};

// src/client/actor-handle.ts
import * as cbor7 from "cbor-x";
import invariant6 from "invariant";

// src/client/raw-utils.ts
import invariant5 from "invariant";
async function rawHttpFetch(driver, actorQuery, params, input, init) {
  let path;
  let mergedInit = init || {};
  if (typeof input === "string") {
    path = input;
  } else if (input instanceof URL) {
    path = input.pathname + input.search;
  } else if (input instanceof Request) {
    const url = new URL(input.url);
    path = url.pathname + url.search;
    const requestHeaders = new Headers(input.headers);
    const initHeaders = new Headers(init?.headers || {});
    const mergedHeaders = new Headers(requestHeaders);
    initHeaders.forEach((value, key) => {
      mergedHeaders.set(key, value);
    });
    mergedInit = {
      method: input.method,
      body: input.body,
      mode: input.mode,
      credentials: input.credentials,
      redirect: input.redirect,
      referrer: input.referrer,
      referrerPolicy: input.referrerPolicy,
      integrity: input.integrity,
      keepalive: input.keepalive,
      signal: input.signal,
      ...mergedInit,
      // init overrides Request properties
      headers: mergedHeaders
      // headers must be set after spread to ensure proper merge
    };
    if (mergedInit.body) {
      mergedInit.duplex = "half";
    }
  } else {
    throw new TypeError("Invalid input type for fetch");
  }
  try {
    const { actorId } = await queryActor(void 0, actorQuery, driver);
    logger2().debug({ msg: "found actor for raw http", actorId });
    invariant5(actorId, "Missing actor ID");
    const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
    const url = new URL(`http://actor/request/${normalizedPath}`);
    const proxyRequestHeaders = new Headers(mergedInit.headers);
    if (params) {
      proxyRequestHeaders.set(HEADER_CONN_PARAMS, JSON.stringify(params));
    }
    const proxyRequest = new Request(url, {
      ...mergedInit,
      headers: proxyRequestHeaders
    });
    return driver.sendRequest(actorId, proxyRequest);
  } catch (err) {
    const { group, code, message, metadata } = deconstructError(
      err,
      logger2(),
      {},
      true
    );
    throw new ActorError2(group, code, message, metadata);
  }
}
async function rawWebSocket(driver, actorQuery, params, path, protocols) {
  const encoding = "bare";
  const { actorId } = await queryActor(void 0, actorQuery, driver);
  logger2().debug({ msg: "found actor for action", actorId });
  invariant5(actorId, "Missing actor ID");
  let pathPortion = "";
  let queryPortion = "";
  if (path) {
    const queryIndex = path.indexOf("?");
    if (queryIndex !== -1) {
      pathPortion = path.substring(0, queryIndex);
      queryPortion = path.substring(queryIndex);
    } else {
      pathPortion = path;
    }
    if (pathPortion.startsWith("/")) {
      pathPortion = pathPortion.slice(1);
    }
  }
  const fullPath = `${PATH_WEBSOCKET_PREFIX}${pathPortion}${queryPortion}`;
  logger2().debug({
    msg: "opening websocket",
    actorId,
    encoding,
    path: fullPath
  });
  const ws = await driver.openWebSocket(fullPath, actorId, encoding, params);
  return ws;
}

// src/client/actor-handle.ts
var ActorHandleRaw = class {
  #client;
  #driver;
  #encoding;
  #actorQuery;
  #params;
  #getParams;
  #queueSender;
  /**
   * Do not call this directly.
   *
   * Creates an instance of ActorHandleRaw.
   *
   * @protected
   */
  constructor(client, driver, params, getParams, encoding, actorQuery) {
    this.#client = client;
    this.#driver = driver;
    this.#encoding = encoding;
    this.#actorQuery = actorQuery;
    this.#params = params;
    this.#getParams = getParams;
    this.#queueSender = createQueueSender({
      encoding: this.#encoding,
      params: this.#params,
      customFetch: async (request) => {
        const { actorId } = await queryActor(
          void 0,
          this.#actorQuery,
          this.#driver
        );
        return this.#driver.sendRequest(actorId, request);
      }
    });
  }
  async #resolveConnectionParams() {
    if (this.#getParams) {
      return await this.#getParams();
    }
    return this.#params;
  }
  send(name, body, options) {
    return this.#queueSender.send(name, body, options);
  }
  /**
   * Call a raw action. This method sends an HTTP request to invoke the named action.
   *
   * @see {@link ActorHandle}
   * @template Args - The type of arguments to pass to the action function.
   * @template Response - The type of the response returned by the action function.
   */
  async action(opts) {
    if (typeof opts === "string" || typeof opts !== "object" || opts === null || !("name" in opts)) {
      throw new Error(
        `Invalid action call: expected an options object { name, args }, got ${typeof opts}. Use handle.actionName(...args) for the shorthand API.`
      );
    }
    let actorId;
    try {
      const result = await queryActor(
        void 0,
        this.#actorQuery,
        this.#driver
      );
      actorId = result.actorId;
      logger2().debug({ msg: "found actor for action", actorId });
      invariant6(actorId, "Missing actor ID");
      logger2().debug({
        msg: "handling action",
        name: opts.name,
        encoding: this.#encoding
      });
      const responseData = await sendHttpRequest({
        url: `http://actor/action/${encodeURIComponent(opts.name)}`,
        method: "POST",
        headers: {
          [HEADER_ENCODING]: this.#encoding,
          ...this.#params !== void 0 ? { [HEADER_CONN_PARAMS]: JSON.stringify(this.#params) } : {}
        },
        body: opts.args,
        encoding: this.#encoding,
        customFetch: this.#driver.sendRequest.bind(
          this.#driver,
          actorId
        ),
        signal: opts?.signal,
        requestVersion: CURRENT_VERSION,
        requestVersionedDataHandler: HTTP_ACTION_REQUEST_VERSIONED,
        responseVersion: CURRENT_VERSION,
        responseVersionedDataHandler: HTTP_ACTION_RESPONSE_VERSIONED,
        requestZodSchema: HttpActionRequestSchema,
        responseZodSchema: HttpActionResponseSchema,
        // JSON Request: args is the raw value
        requestToJson: (args) => ({
          args
        }),
        // BARE Request: args needs to be CBOR-encoded
        requestToBare: (args) => ({
          args: bufferToArrayBuffer(cbor7.encode(args))
        }),
        // JSON Response: output is the raw value
        responseFromJson: (json) => json.output,
        // BARE Response: output is ArrayBuffer that needs CBOR-decoding
        responseFromBare: (bare4) => cbor7.decode(new Uint8Array(bare4.output))
      });
      return responseData;
    } catch (err) {
      const { group, code, message, metadata } = deconstructError(
        err,
        logger2(),
        {},
        true
      );
      if (actorId && isSchedulingError(group, code)) {
        const schedulingError = await checkForSchedulingError(
          group,
          code,
          actorId,
          this.#actorQuery,
          this.#driver
        );
        if (schedulingError) {
          throw schedulingError;
        }
      }
      throw new ActorError2(group, code, message, metadata);
    }
  }
  /**
   * Establishes a persistent connection to the actor.
   *
   * @template AD The actor class that this connection is for.
   * @returns {ActorConn<AD>} A connection to the actor.
   */
  connect() {
    logger2().debug({
      msg: "establishing connection from handle",
      query: this.#actorQuery
    });
    const conn = new ActorConnRaw(
      this.#client,
      this.#driver,
      this.#params,
      this.#getParams,
      this.#encoding,
      this.#actorQuery
    );
    return this.#client[CREATE_ACTOR_CONN_PROXY](
      conn
    );
  }
  /**
   * Fetches a resource from this actor via the /request endpoint. This is a
   * convenience wrapper around the raw HTTP API.
   */
  fetch(input, init) {
    return rawHttpFetch(
      this.#driver,
      this.#actorQuery,
      this.#params,
      input,
      init
    );
  }
  /**
   * Opens a raw WebSocket connection to this actor.
   */
  async webSocket(path, protocols) {
    const params = await this.#resolveConnectionParams();
    return rawWebSocket(
      this.#driver,
      this.#actorQuery,
      params,
      path,
      protocols
    );
  }
  /**
   * Resolves the actor to get its unique actor ID.
   */
  async resolve() {
    if ("getForKey" in this.#actorQuery) {
      const name = this.#actorQuery.getForKey.name;
      const { actorId } = await queryActor(
        void 0,
        this.#actorQuery,
        this.#driver
      );
      this.#actorQuery = { getForId: { actorId, name } };
      return actorId;
    } else if ("getOrCreateForKey" in this.#actorQuery) {
      const name = this.#actorQuery.getOrCreateForKey.name;
      const { actorId } = await queryActor(
        void 0,
        this.#actorQuery,
        this.#driver
      );
      this.#actorQuery = { getForId: { actorId, name } };
      return actorId;
    } else if ("getForId" in this.#actorQuery) {
      return this.#actorQuery.getForId.actorId;
    } else if ("create" in this.#actorQuery) {
      invariant6(false, "actorQuery cannot be create");
    } else {
      assertUnreachable2(this.#actorQuery);
    }
  }
  /**
   * Returns the raw URL for routing traffic to the actor.
   */
  async getGatewayUrl() {
    const { actorId } = await queryActor(
      void 0,
      this.#actorQuery,
      this.#driver
    );
    return await this.#driver.buildGatewayUrl(actorId);
  }
};

// src/client/client.ts
var ACTOR_CONNS_SYMBOL = /* @__PURE__ */ Symbol("actorConns");
var CREATE_ACTOR_CONN_PROXY = /* @__PURE__ */ Symbol("createActorConnProxy");
var ClientRaw = class {
  #disposed = false;
  [ACTOR_CONNS_SYMBOL] = /* @__PURE__ */ new Set();
  #driver;
  #encodingKind;
  /**
   * Creates an instance of Client.
   */
  constructor(driver, encoding) {
    this.#driver = driver;
    this.#encodingKind = encoding ?? "bare";
  }
  /**
   * Gets a stateless handle to a actor by its ID.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string} actorId - The ID of the actor.
   * @param {GetWithIdOptions} [opts] - Options for getting the actor.
   * @returns {ActorHandle<AD>} - A handle to the actor.
   */
  getForId(name, actorId, opts) {
    logger2().debug({
      msg: "get handle to actor with id",
      name,
      actorId,
      params: opts?.params
    });
    const actorQuery = {
      getForId: {
        name,
        actorId
      }
    };
    const handle = this.#createHandle(
      opts?.params,
      opts?.getParams,
      actorQuery
    );
    return createActorProxy(handle);
  }
  /**
   * Gets a stateless handle to a actor by its key, but does not create the actor if it doesn't exist.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string | string[]} [key=[]] - The key to identify the actor. Can be a single string or an array of strings.
   * @param {GetWithIdOptions} [opts] - Options for getting the actor.
   * @returns {ActorHandle<AD>} - A handle to the actor.
   */
  get(name, key, opts) {
    const keyArray = typeof key === "string" ? [key] : key || [];
    logger2().debug({
      msg: "get handle to actor",
      name,
      key: keyArray,
      parameters: opts?.params
    });
    const actorQuery = {
      getForKey: {
        name,
        key: keyArray
      }
    };
    const handle = this.#createHandle(
      opts?.params,
      opts?.getParams,
      actorQuery
    );
    return createActorProxy(handle);
  }
  /**
   * Gets a stateless handle to a actor by its key, creating it if necessary.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string | string[]} [key=[]] - The key to identify the actor. Can be a single string or an array of strings.
   * @param {GetOptions} [opts] - Options for getting the actor.
   * @returns {ActorHandle<AD>} - A handle to the actor.
   */
  getOrCreate(name, key, opts) {
    const keyArray = typeof key === "string" ? [key] : key || [];
    logger2().debug({
      msg: "get or create handle to actor",
      name,
      key: keyArray,
      parameters: opts?.params,
      createInRegion: opts?.createInRegion
    });
    const actorQuery = {
      getOrCreateForKey: {
        name,
        key: keyArray,
        input: opts?.createWithInput,
        region: opts?.createInRegion
      }
    };
    const handle = this.#createHandle(
      opts?.params,
      opts?.getParams,
      actorQuery
    );
    return createActorProxy(handle);
  }
  /**
   * Creates a new actor with the provided key and returns a stateless handle to it.
   * Resolves the actor ID and returns a handle with getForId query.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string | string[]} key - The key to identify the actor. Can be a single string or an array of strings.
   * @param {CreateOptions} [opts] - Options for creating the actor (excluding name and key).
   * @returns {Promise<ActorHandle<AD>>} - A promise that resolves to a handle to the actor.
   */
  async create(name, key, opts) {
    const keyArray = typeof key === "string" ? [key] : key || [];
    const createQuery = {
      create: {
        ...opts,
        // Do these last to override `opts`
        name,
        key: keyArray
      }
    };
    logger2().debug({
      msg: "create actor handle",
      name,
      key: keyArray,
      parameters: opts?.params,
      create: createQuery.create
    });
    const { actorId } = await queryActor(
      void 0,
      createQuery,
      this.#driver
    );
    logger2().debug({
      msg: "created actor with ID",
      name,
      key: keyArray,
      actorId
    });
    const getForIdQuery = {
      getForId: {
        name,
        actorId
      }
    };
    const handle = this.#createHandle(
      opts?.params,
      opts?.getParams,
      getForIdQuery
    );
    const proxy = createActorProxy(handle);
    return proxy;
  }
  #createHandle(params, getParams, actorQuery) {
    return new ActorHandleRaw(
      this,
      this.#driver,
      params,
      getParams,
      this.#encodingKind,
      actorQuery
    );
  }
  [CREATE_ACTOR_CONN_PROXY](conn) {
    this[ACTOR_CONNS_SYMBOL].add(conn);
    conn[CONNECT_SYMBOL]();
    return createActorProxy(conn);
  }
  /**
   * Disconnects from all actors.
   *
   * @returns {Promise<void>} A promise that resolves when all connections are closed.
   */
  async dispose() {
    if (this.#disposed) {
      logger2().warn({ msg: "client already disconnected" });
      return;
    }
    this.#disposed = true;
    logger2().debug({ msg: "disposing client" });
    const disposePromises = [];
    for (const conn of this[ACTOR_CONNS_SYMBOL].values()) {
      disposePromises.push(conn.dispose());
    }
    await Promise.all(disposePromises);
  }
};
function createClientWithDriver(driver, config4 = {}) {
  const client = new ClientRaw(driver, config4.encoding);
  return new Proxy(client, {
    get: (target, prop, receiver) => {
      if (typeof prop === "symbol" || prop in target) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value === "function") {
          return value.bind(target);
        }
        return value;
      }
      if (typeof prop === "string") {
        return {
          // Handle methods (stateless action)
          get: (key, opts) => {
            return target.get(prop, key, opts);
          },
          getOrCreate: (key, opts) => {
            return target.getOrCreate(prop, key, opts);
          },
          getForId: (actorId, opts) => {
            return target.getForId(prop, actorId, opts);
          },
          create: async (key, opts = {}) => {
            return await target.create(prop, key, opts);
          }
        };
      }
      return void 0;
    }
  });
}
function createActorProxy(handle) {
  const methodCache = /* @__PURE__ */ new Map();
  return new Proxy(handle, {
    get(target, prop, receiver) {
      if (typeof prop === "symbol") {
        return Reflect.get(target, prop, receiver);
      }
      if (prop === "constructor" || prop in target) {
        const value = Reflect.get(target, prop, target);
        if (typeof value === "function") {
          return value.bind(target);
        }
        return value;
      }
      if (typeof prop === "string") {
        if (prop === "then") return void 0;
        let method = methodCache.get(prop);
        if (!method) {
          method = (...args) => target.action({ name: prop, args });
          methodCache.set(prop, method);
        }
        return method;
      }
    },
    // Support for 'in' operator
    has(target, prop) {
      if (typeof prop === "string") return true;
      return Reflect.has(target, prop);
    },
    // Support instanceof checks
    getPrototypeOf(target) {
      return Reflect.getPrototypeOf(target);
    },
    // Prevent property enumeration of non-existent action methods
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    // Support proper property descriptors
    getOwnPropertyDescriptor(target, prop) {
      const targetDescriptor = Reflect.getOwnPropertyDescriptor(
        target,
        prop
      );
      if (targetDescriptor) {
        return targetDescriptor;
      }
      if (typeof prop === "string") {
        return {
          configurable: true,
          enumerable: false,
          writable: false,
          value: (...args) => target.action({ name: prop, args })
        };
      }
      return void 0;
    }
  });
}

// src/client/config.ts
import z5 from "zod/v4";
var DEFAULT_ENDPOINT = "http://localhost:6420";
var hasWarnedMissingEndpoint = false;
var ClientConfigSchemaBase = z5.object({
  /**
   * Endpoint to connect to for Rivet Engine or RivetKit manager API.
   *
   * Supports URL auth syntax for namespace and token:
   * - `https://namespace:token@api.rivet.dev`
   * - `https://namespace@api.rivet.dev`
   *
   * Can also be set via RIVET_ENDPOINT environment variables.
   *
   * Defaults to http://localhost:6420.
   */
  endpoint: z5.string().optional().transform((val) => {
    const resolved = val ?? getRivetEngine() ?? getRivetEndpoint();
    if (!resolved && !hasWarnedMissingEndpoint) {
      hasWarnedMissingEndpoint = true;
      console.warn(
        `[rivetkit] No endpoint provided to client. Defaulting to ${DEFAULT_ENDPOINT}. Starting in 2.2.0, an explicit endpoint will be required. Pass an endpoint to createClient() or createRivetKit(), or set the RIVET_ENDPOINT environment variable.`
      );
    }
    return resolved ?? DEFAULT_ENDPOINT;
  }),
  /** Token to use to authenticate with the API. */
  token: z5.string().optional().transform((val) => val ?? getRivetToken()),
  /** Namespace to connect to. */
  namespace: z5.string().optional().transform((val) => val ?? getRivetNamespace()),
  /** Name of the runner. This is used to group together runners in to different pools. */
  runnerName: z5.string().default(() => getRivetRunner() ?? "default"),
  encoding: EncodingSchema.default("bare"),
  headers: z5.record(z5.string(), z5.string()).optional().default(() => ({})),
  // See RunConfig.getUpgradeWebSocket
  //
  // This is required in the client config in order to support
  // `proxyWebSocket`
  getUpgradeWebSocket: z5.custom().optional(),
  /** Whether to automatically perform health checks when the client is created. */
  disableMetadataLookup: z5.boolean().optional().default(false),
  /** Whether to enable RivetKit Devtools integration. */
  devtools: z5.boolean().default(
    () => typeof window !== "undefined" && (window?.location?.hostname === "127.0.0.1" || window.location?.hostname === "localhost")
  )
});
var ClientConfigSchema = ClientConfigSchemaBase.transform(
  (config4, ctx) => transformClientConfig(config4, ctx)
);
function transformClientConfig(config4, ctx) {
  const parsedEndpoint = tryParseEndpoint(ctx, {
    endpoint: config4.endpoint,
    path: ["endpoint"],
    namespace: config4.namespace,
    token: config4.token
  });
  return {
    ...config4,
    endpoint: parsedEndpoint?.endpoint,
    namespace: parsedEndpoint?.namespace ?? config4.namespace ?? "default",
    token: parsedEndpoint?.token ?? config4.token
  };
}

// src/client/mod.ts
function createClient(endpointOrConfig) {
  const configInput = endpointOrConfig === void 0 ? {} : typeof endpointOrConfig === "string" ? { endpoint: endpointOrConfig } : endpointOrConfig;
  const config4 = ClientConfigSchema.parse(configInput);
  const driver = new RemoteManagerDriver(config4);
  if (config4.devtools) {
    injectDevtools(config4);
  }
  return createClientWithDriver(driver, config4);
}
export {
  ActorClientError,
  ActorConnDisposed,
  ActorConnRaw,
  ActorError2 as ActorError,
  ActorHandleRaw,
  InternalError2 as InternalError,
  KEYS as KV_KEYS,
  MalformedResponseMessage,
  ManagerError,
  createClient
};
//# sourceMappingURL=client.js.map